/**
 * EventHub Backend API Service
 *
 * Handles communication with the adg-backend API for event operations.
 * Authenticates with `Authorization: Bearer <upstream JWT>` — either an
 * Entra id_token or a SuperTokens access token, picked per session by
 * `getUpstreamJwt()`. Replaced the legacy shared `x-api-key` flow which
 * shipped a global secret in the bundle.
 */

import { getUpstreamJwt } from '@/utils/jwtToken'
import { invalidateRef } from '@/services/refDataCache'
import type { StoredParticipantType } from '@/utils/storedParticipantType'
import { invalidateMewsCompanies } from '@/services/mewsCompanyCache'

/**
 * Backend routes that must NEVER carry an Authorization header.
 *
 * These run before a session exists (or while the current one is being
 * renewed) and authenticate from the request body instead — an auth `code`
 * for /entra/token, a `refresh_token` for /entra/refresh. The backend mounts
 * them without `jwtAuth`, so the header is not merely unnecessary: asking for
 * one re-enters `getEntraIdToken()` from inside its own in-flight refresh
 * (getEntraIdToken → backendApi.request → getUpstreamJwt → getEntraIdToken),
 * which recursed until the stack blew.
 *
 * Deliberately an exact-path allowlist, NOT an `/api/auth/*` prefix: the
 * backend's /api/auth/me, /db-jwt, /test-jwt and /admin-test DO sit behind
 * `jwtAuth` and must keep sending their token.
 */
export interface ResolvedSetting {
  key: string
  scope: 'system' | 'property'
  type: 'boolean' | 'number' | 'string' | 'id'
  group: string
  label: string
  description: string
  /** Always null for a secret — see `configured`. */
  value: string | null
  /** db = stored here · env = from the container env · default = registry · missing = nothing answers it */
  source: 'db' | 'env' | 'default' | 'missing'
  configured: boolean
  /** Declared required and nothing resolves it. */
  missing: boolean
  secret: boolean
  /** Takes effect without a restart. */
  liveReload: boolean
  env?: string
  default?: string | null
  updated_at?: string | null
  updated_by?: string | null
}

// ── Background workers (admin) ──────────────────────────────────────────
export interface WorkerRunRow {
  id: number
  worker: string
  started_at: string
  finished_at: string | null
  duration_ms: number | null
  outcome: 'ok' | 'error' | 'skipped'
  processed: number
  failed: number
  skip_reason: string | null
  error: string | null
  detail: Record<string, unknown> | null
  interval_ms: number | null
}

export interface WorkerLive {
  enabled: boolean | null
  running: boolean | null
  health: 'green' | 'yellow' | 'red' | null
  is_ticking: boolean | null
  tick_interval_ms: number | null
  next_run_at: string | null
  last_tick_at: string | null
}

export interface WorkerRow {
  name: string
  label: string
  description: string
  /** Tables it writes besides worker_runs — where the per-item detail lives. */
  writes: string[]
  enabled_key: string
  enabled_setting: ResolvedSetting | null
  tuning: ResolvedSetting[]
  /** null when THIS container never constructed the worker. */
  live: WorkerLive | null
  detail: Record<string, unknown> | null
  last_run: WorkerRunRow | null
  /** Fallback for a replica with no live instance; an estimate, not a promise. */
  next_run_estimate: string | null
  last_24h: { runs: number; errors: number; skipped: number; processed: number; failed: number; avg_ms: number | null }
}

export interface WorkersResponse {
  workers: WorkerRow[]
  /** False when no worker runs in the container that answered — say so rather than showing seven dead cards. */
  instance_has_workers: boolean
  timestamp: string
}

export interface SettingsCatalogue {
  property_id: number
  settings: ResolvedSetting[]
  summary: {
    total: number; missing: number; from_db: number
    from_env: number; from_default: number; unset: number
  }
}

const PRE_SESSION_ROUTES = new Set<string>([
  '/api/auth/entra/token',
  '/api/auth/entra/refresh',
])

const BACKEND_API_URL = import.meta.env.VITE_BACKEND_API_URL !== undefined
  ? import.meta.env.VITE_BACKEND_API_URL
  : 'https://adg-backend.app.kernboards.com'

// The property the single-property surfaces (reception board, Maya, global
// search, tasks) work on. The client's ADG deployment is property 2 — the
// historical hardcode, kept as the fallback; a stage whose property has a
// different id (Hetzner native PoC: 1) sets VITE_DEFAULT_PROPERTY_ID.
export const PROPERTY_ID = Number(import.meta.env.VITE_DEFAULT_PROPERTY_ID) || 2

export interface EventAckState {
  event_id: number
  content_version: number
  seen_version: number | null
  printed_version: number | null
  seen_at: string | null
  printed_at: string | null
  reviewed: boolean
  stale: boolean
}

/**
 * Global function-sheet "checked" lock — one per event, shared across all
 * users. Separate from EventAckState (that is the per-user notification-bell
 * review). `checked` is true only while the check matches the current version;
 * `stale` means the event changed after being checked.
 */
/** Session "gültig" (V203) — per session day-row. */
export interface SessionValidState {
  session_id: number
  valid: boolean
  valid_by: string | null
  valid_at: string | null
}

/** Event "gültig" (V202) — global per-event sign-off by the event managers. */
export interface EventValidState {
  event_id: number
  valid: boolean
  valid_by: string | null
  valid_at: string | null
}

export interface FunctionSheetCheckState {
  event_id: number
  content_version: number
  checked: boolean
  stale: boolean
  checked_by: string | null
  checked_at: string | null
  checked_version: number | null
}

export interface AppNotification {
  id: number
  event_id: number | null
  report_id: string | null
  task_id: number | null
  type: string
  title: string | null
  body: string | null
  from_version: number | null
  to_version: number | null
  read_at: string | null
  created_at: string
  event_name: string | null
}

export type RateGuestKind = 'hotel' | 'dayguest' | 'lecturer'

export interface ApplicableRatesQuery {
  property_id: number
  start_date: string
  end_date: string
  kind: RateGuestKind
  room_type_id?: number
  stream?: string
}

export interface RateRestrictionResult {
  id: string
  origin: string
  blocks: boolean
  exception_satisfied: boolean
  conditions: {
    type: string
    exact_rate_id: string | null
    base_rate_id: string | null
    rate_group_id: string | null
    resource_category_id: string | null
    resource_category_type: string | null
    start_utc: string | null
    end_utc: string | null
    days: string[]
  }
  exceptions: {
    min_advance: string | null
    max_advance: string | null
    min_length: string | null
    max_length: string | null
    min_price: { currency: string; gross_value: number } | null
    max_price: { currency: string; gross_value: number } | null
  } | null
}

/** One Storno tier as the backend's mirror read returns it. */
export interface StornoRuleView {
  id: number
  mews_policy_id: string
  name: string | null
  applicability: string | null
  applicability_offset: string | null
  deadline: import('@/utils/stornoPolicy').StornoDeadline
  relative_fee: number
  absolute_fee: { value: number; currency: string } | null
  fee_extents: string[]
  fee_maximum_time_units: number | null
  is_active: boolean
}

/** GET /api/pms/cancellation-policies/rules — see cancellationPolicyService.listStornoRules. */
export interface StornoRulesResponse {
  property_id: number
  synced_at: string | null
  catalogue: Array<StornoRuleView & { is_portfolio_managed: boolean }>
  rate_groups: Array<{
    mews_rate_group_id: string
    name: string | null
    is_active: boolean
    service_name: string | null
    arrival_time: string | null
    rates: Array<{ id: number; name: string; is_active: boolean }>
    tiers: StornoRuleView[]
  }>
  /** Rates whose group has no rule in MEWS — NOT free cancellation. */
  rates_without_rule: Array<{ id: number; name: string; is_active: boolean; mews_rate_group_id: string | null }>
}

export interface RateCancellationPolicyResult {
  applicability: string
  fee_extents: string[]
  applicability_offset: string
  fee_maximum_time_units: number | null
  absolute_fee: { currency: string; value: number } | null
  relative_fee: number
}

export interface ApplicableRateResult {
  id: number
  name: string
  stream: string | null
  mews_rate_id: string | null
  applicable: boolean
  applicability_code: string
  mews: {
    is_active: boolean | null
    is_enabled: boolean | null
    is_public: boolean | null
    type: string | null
    base_rate_id: string | null
  } | null
  restrictions: RateRestrictionResult[]
  blocking_restriction_ids: string[]
  pricing: {
    status: 'ok' | 'unavailable'
    currency: string
    nightly: Array<{ start_utc: string | null; gross: number | null; net: number | null }>
    total_gross: number | null
  } | null
  pricing_error: string | null
  cancellation_policies: RateCancellationPolicyResult[]
}

export interface ApplicableRatesResponse {
  property_id: number
  service_id: string
  criteria: {
    start_date: string
    end_date: string
    kind: RateGuestKind
    stream: string | null
    room_type_id: number | null
    mews_category_id: string | null
    name: string | null
  }
  fetched_at: string
  restrictions_status: 'ok' | 'unavailable'
  cancellation_policies_status: 'ok' | 'unavailable'
  cancellation_policies_warning: { code: string; message: string } | null
  applicable_rate_ids: number[]
  rates: ApplicableRateResult[]
  cache: { hit: boolean; expires_at: string }
}

interface UpsertEventPayload {
  id?: string | number
  property_id?: string | number
  name?: string
  description: string
  description_2?: string
  start_date: string
  end_date: string
  status?: string
  event_stream?: string
  event_type?: string
  participation_type?: string
  external_id?: string
  start_time?: string
  end_time?: string
  status_external?: string
  min_participants?: number
  max_participants?: number
  overnight_guests?: number
  source_system?: string
  source_id?: string
  source_modified_at?: string
  source_data?: Record<string, any> | null
  notes?: string
  customer_id?: string | null
  sales_person_id?: string | null
  product_manager_id?: string | null
  event_manager_id?: string | null
  pre_evening_arrival?: boolean | null
  series_id?: string | number | null
  release_date?: string | null
  signage_line_3?: string | null
  signage_logo?: string | null
  // Legacy single-tier room type — 'comfort' | 'economy'. Superseded by
  // `room_categories` below (V-recent), which lets a single event
  // request multiple pms_room_types with per-row counts. The backend
  // still accepts `room_type` for backward compat, but prefers
  // `room_categories` when present (the two shouldn't be sent together).
  room_type?: 'comfort' | 'economy' | null
  // Modern multi-category shape. Each entry names a specific room type
  // (FK to pms_room_types.id) and how many rooms of that type the event
  // needs. Sum of counts becomes `overnight_guests` for legacy readers.
  //
  // Blocks are keyed by ROOM CATEGORY: the backend holds exactly one MEWS
  // availability block per entry here. There is no rate dimension — rate
  // variance lives on the individual reservation (`rate_id` on a pickup), not
  // on a second block. Sending a rate here does nothing.
  //
  // Per stream: MICE takes any number of categories, internal takes exactly
  // one, and bs/seminars 422 if this is sent at all.
  //
  //   room_categories: [
  //     { room_type_id: 12, count: 5 },
  //     { room_type_id: 15, count: 5 },
  //   ]
  room_categories?: Array<{ room_type_id: number; count: number }>
  space_ids?: number[]
  [key: string]: any
}

// Thrown by request() for any non-OK response. Carries the structured error
// payload so callers can inspect e.g. code === 'BLOCK_AVAILABILITY_SHORTFALL'
// and render shortfall details without re-parsing the message string.
export class BackendApiError extends Error {
  status: number
  code?: string
  details?: any
  /**
   * The backend's CLASSIFIED cause (`failure_code`, from mewsErrorClassifier).
   * Distinct from `code`, which on the pickup path is often the catch-all
   * `AUTO_DETACH_FAILED`: the route only promotes the classified value into
   * `code` when it names a known upstream fault, so for every other cause the
   * real reason arrives here and nowhere else. Dropping it on the floor is what
   * made a MEWS restriction refusal read as an untyped English stack string.
   */
  failureCode?: string
  constructor(
    message: string,
    status: number,
    code?: string,
    details?: any,
    failureCode?: string,
  ) {
    super(message)
    this.name = 'BackendApiError'
    this.status = status
    this.code = code
    this.details = details
    this.failureCode = failureCode
  }
}

// ─── Releasable-rooms verification (POST /api/events/blocks/releasable) ──────
// Mirrors the backend's releasableRooms.ts contract exactly.
export interface BlockReleasable {
  blockId: number
  eventId: number | null
  externalBlockId: string | null
  /** What OUR database thinks the block still holds. */
  dbRoomCount: number
  /**
   * What MEWS actually holds — already net of pickups, because the backend
   * pushes `-max(0, rooms_requested − consumed)` as the adjustment. Null when
   * MEWS could not be read, and a null block must make the whole event a skip.
   */
  mewsHeld: number | null
  consumed: number
  /** max(0, mewsHeld − consumed). Zero whenever MEWS is unknown. */
  releasable: number
  /** dbRoomCount − mewsHeld when both are known. Non-zero means drift. */
  drift: number | null
  /** Set when this block could not be verified, or holds nothing to verify. */
  unverifiedReason?: string
}

export interface EventReleasable {
  eventId: number
  releasable: number
  blocks: BlockReleasable[]
  /** True when any block could not be verified against MEWS. */
  partial: boolean
  /** True when DB and MEWS disagree on at least one block. */
  hasDrift: boolean
  /** Reduction-protected events report zero releasable and are flagged. */
  protected?: boolean
  protected_by?: string | null
  protected_reason?: string | null
}

export interface ReleasableResponse {
  status: 'ok'
  events: EventReleasable[]
  total_releasable: number
  partial: boolean
  has_drift: boolean
  /**
   * Events whose MEWS verification could not be completed AT ALL — the request
   * itself failed (502 / timeout / network), not a per-block soft failure
   * (that surfaces as `partial`). Empty on a clean verify. Callers release the
   * events that DID verify and show these as skipped, rather than abandoning
   * the whole batch (EH-0350 / option A).
   */
  failedEventIds: number[]
}

// ─── Per-block resize (POST /api/events/blocks/bulkResize, `events` form) ────
export interface BlockResizeTarget {
  block_id: number
  /** Desired REMAINING HOLD for this block, not the intent. */
  target: number
}

export interface BlockResizeOutcome {
  blockId: number
  ok: boolean
  oldRoomCount?: number
  newRoomCount?: number
  reason?: string
}

export interface PerBlockEventResult {
  ok: boolean
  eventId: number
  blocks: BlockResizeOutcome[]
  /**
   * Event-level failure (protection refusal, a block belonging to another
   * event). Absent when the event failed only because individual blocks did —
   * those reasons live on `blocks[]`, so never report from this alone.
   */
  reason?: string
}

export interface PerBlockResizeResponse {
  status: 'ok'
  mode: 'per_block'
  updated: PerBlockEventResult[]
  /** An ARRAY here, unlike the legacy form where `failed` is a count. */
  failed: PerBlockEventResult[]
}

export interface BlockShortfall {
  code: 'BLOCK_AVAILABILITY_SHORTFALL'
  message: string
  recoverable?: boolean
  details?: {
    room_type?: string
    category_id?: string
    category_name?: string
    requested?: number
    available?: number
    shortfall?: number
    tightest_day?: string
    per_day?: any
  }
}

interface BackendEventResponse {
  message?: string
  // 'blocked' = the save was REFUSED, not applied. Raised when a MICE event is
  // moved to `proposal` while some requested room category still has no
  // approved/created block. `pmsBlockError` carries the reason. Treating this
  // as success closes the modal on a save that never happened.
  action: 'created' | 'updated' | 'blocked'
  event: Record<string, any>
  pms_block_id?: string
  pms_block_status?: string
  pmsBlock?: Record<string, any>
  // One entry per requested room category (one MEWS block each).
  pmsBlocks?: PmsBlock[]
  pmsBlockError?: string
  // No-availability is a SUCCESS-with-caveat: the event was saved but the hotel
  // block couldn't be held at full size. The shortfall rides here on a 2xx body
  // (was previously a 422 error); `warnings` lists the non-error warning codes.
  blockShortfall?: BlockShortfall
  // Multi-category events can fall short in more than one category at once.
  // `blockShortfall` is only the first of these — read this array to show them all.
  blockShortfalls?: BlockShortfall[]
  reconciliation?: { pms?: { status?: 'booked' | 'needs_reconciliation' | 'not_booked' } }
  warnings?: string[]
}

/**
 * A PMS block as the backend returns it.
 *
 * One block per ROOM CATEGORY per event (not per rate — the Mitglied /
 * NichtMitglied block pair is gone). The FK columns `room_type_id` / `rate_id`
 * replaced the old free-text `room_category` / `rate`, which no longer exist;
 * `mews_category_id`, `room_type_tier`, `room_type_name` and `rate_name` are
 * derived server-side from those FKs.
 *
 * ── The two numbers, and which one you want ────────────────────────────────
 *
 *   rooms_requested — what the operator ASKED FOR. Changes only when someone
 *                     edits the room mix. THIS is the "Y" in "X of Y picked up".
 *   room_count      — what the block still HOLDS in MEWS. Derived:
 *                     max(0, rooms_requested − picked_up). Shrinks with every
 *                     pickup. It is NOT the block's size.
 *
 * A block of 10 with 3 pickups reads `rooms_requested: 10, room_count: 7,
 * picked_up: 3`. Rendering picked_up over room_count would say "3 of 7".
 */
export interface PmsBlock {
  id: number
  event_id: number
  property_id: number
  is_primary: boolean
  external_block_id: string | null
  name: string | null
  /** DERIVED: rooms still held in MEWS = max(0, rooms_requested − picked_up). Not the block's size. */
  room_count: number
  /** INTENT: how many rooms of this category the event asked for. The denominator. */
  rooms_requested: number
  arrival_date: string | null
  departure_date: string | null
  room_type_id: number | null
  rate_id: number | null
  status: PmsBlockStatus
  notes?: string | null
  last_action?: string | null
  last_action_at?: string | null
  // Derived server-side via the FKs.
  mews_category_id?: string | null
  room_type_tier?: 'comfort' | 'economy' | null
  room_type_name?: string | null
  rate_name?: string | null
  /** Live reservations that took a slot from this block (their debited_block_id points here). */
  picked_up?: number
}

export interface ManageEventBlockPayload {
  block_id?: number
  /** Absolute rooms still available for future pickup, not a delta or total. */
  available_rooms?: number
  /** First held night (YYYY-MM-DD). */
  arrival_date?: string
  /** Last held night, inclusive (YYYY-MM-DD). */
  departure_date?: string
  room_type_id?: number
  notes?: string | null
  reason?: string | null
}

export interface ManagedBlockResponse {
  action: 'created' | 'updated' | 'extended' | 'category_changed' | 'period_replaced' | 'deleted' | 'already_deleted' | 'emptied'
  event_id: number
  block: PmsBlock | null
  retired_block?: PmsBlock | null
  available_rooms?: number
  consumed_rooms?: number
  consumed_rooms_preserved?: number
  rooms_requested?: number
  released_available_rooms?: number
  event_overnight_guests?: number
  availability_check?: {
    required?: boolean
    ready?: boolean
    ok: boolean
    source?: 'snapshot' | 'live'
    snapshot_fetched_at?: string | null
    available_min?: number | null
    shortfall?: number
    tightest_day?: string | null
  }
}

/**
 * `new`               — MICE inquiry draft. NO MEWS hold exists at all.
 * `requested`         — awaiting approval (MICE qualify, or an approval-gated stream).
 * `created`           — real MEWS availability block held.
 * `rejected`          — approval refused; terminal. Pickups against it 422.
 * `no_availability`   — MEWS had no rooms; stays approvable/retryable.
 * `released`          — hold released hotel-side. Terminal; heal must not re-inflate it.
 * `delete_pending`    — MEWS delete failed; awaiting sweep.
 * `error` / `pending` / `canceled`
 */
export type PmsBlockStatus =
  | 'new' | 'requested' | 'rejected' | 'created' | 'error'
  | 'no_availability' | 'released' | 'delete_pending' | 'pending' | 'canceled'

/**
 * GET /api/events/:id/hotel-summary — the authoritative hotel readout.
 *
 * Use this instead of re-deriving occupancy on the client. Under the detached
 * model a reservation is never attached to a MEWS block, so reading
 * `pms_block_id` / `mews_availability_block_id` off a reservation row to decide
 * "is this picked up" yields NULL for everyone and silently reports 0.
 *
 * Buckets are mutually exclusive:
 *   sum(block_groups[].picked_up) + off_block.total + errored.total + online === totals.active
 */
export interface HotelSummaryBlockGroup {
  block_id: number
  external_block_id: string | null
  status: PmsBlockStatus | null
  mews_category_id: string | null
  room_type_tier: 'comfort' | 'economy' | null
  room_type_name: string | null
  rate_label: string | null
  /** DERIVED: rooms still held in MEWS. Not the block's size. */
  room_count: number
  /** Reservations that took a slot from this block. */
  picked_up: number
  /** The block's requested size — `pms_blocks.rooms_requested`. The "of Y" in "X of Y". */
  rooms_total: number
}

/** GET /api/events/:id */
export interface EventDetail {
  event: Record<string, any>
  pmsBlocks: PmsBlock[]
  reservations: any[]
  spaceBookings: any[]
  participants: {
    total: number
    hotel_guests: number
    external: number
    dayguests: number
    lecturers: number
    /**
     * The operator-set invariant:
     *   overnight_guests === block_size_total + off_block_overnight_count
     * It balances because a pickup shrinks the block by 1 AND moves the guest
     * off-block — the two sides move together. `invariant_holds: false` is the
     * real "this event has drifted" signal; don't re-derive it on the client.
     */
    overnight: {
      event_overnight_guests: number
      block_size_total: number
      off_block_overnight_count: number
      block_plus_offblock: number
      invariant_holds: boolean
      invariant_delta: number
      excluded_lecturers: number
      excluded_dayguests: number
      excluded_externals: number
      excluded_online: number
    }
  }
}

export interface HotelSummary {
  block_groups: HotelSummaryBlockGroup[]
  /** Reservations that consumed NO block slot — they don't reduce a block. */
  off_block: {
    lecturers: number
    dayguests_with_room: number
    external_with_room: number
    /** Hotel guests holding a room that took no block slot: legacy rows, or creates made when the block was exhausted. */
    detached: number
    total: number
  }
  errored: { failed: number; capacity_exceeded: number; total: number }
  online: number
  totals: { active: number; in_mews: number; with_room: number }
}

interface DeleteEventResponse {
  status: string
  message: string
  details: {
    mews_blocks_deleted: number
    db_reservations_deleted: number
    db_blocks_deleted: number
    space_bookings_deleted: boolean
  }
}

// Optional metadata for POST /api/events/:id/cancel. Omit entirely for a
// plain teardown (events.vue red-X). MICE supplies status + cancellation
// reason; the backend jsonb-merges it into events.source_data.cancellation
// atomically with the MEWS teardown.
export interface CancelEventOptions {
  status?: 'cancelled' | 'closed-lost'
  reason?: string
  reasonId?: number | null
  cancellationType?: 'adg' | 'customer'
  by?: number | null
}

export interface CancelEventResponse {
  status: 'cancelled' | 'already_cancelled' | 'not_found' | 'error'
  event?: any
  details: {
    mews_reservations_canceled: number
    mews_blocks_deleted: number
    pms_reservations_marked: number
    pms_blocks_marked: number
    space_bookings_marked: number
    products_marked: number
    packages_marked: number
    errors: string[]
  }
}

export interface HealEventCheck {
  check: string
  status: string
  message: string
}

export interface HealEventBlock {
  id: number
  name: string
  is_primary: boolean
  room_count: number
  external_block_id: string
}

export interface HealEventResponse {
  status: string
  event_id: number
  external_id?: string
  overnight_guests: number
  checks: HealEventCheck[]
  blocks: HealEventBlock[]
}

/**
 * POST /api/events/pickupReservation.
 *
 * "Pickup" is our word, not MEWS'. The backend creates a STANDALONE (detached)
 * MEWS reservation — no AvailabilityBlockId — and debits the event's block for
 * the chosen room category by 1. Nothing is ever attached to a MEWS block.
 */
export interface PickupReservationPayload {
  event_id: number
  customer_id: string
  start_date: string
  end_date: string
  adult_count?: number
  child_count?: number
  guest_name?: string
  email?: string
  contact_no?: string
  notes?: string
  // Which category's block gets debited. Prefer these two over room_type_code.
  room_category_key?: 'economy' | 'komfort'
  occupancy?: 'single' | 'double'
  /** true = never silently downgrade to another category; 422 instead. */
  strict_room_type?: boolean
  /** @deprecated Legacy tier selector. Use room_category_key + occupancy. */
  room_type_code?: string
  room_preference?: string
  type_of_stay?: string
  pre_evening_arrival?: boolean
  payment_method?: string
  company_no?: string
  company_name?: string
  /** Stream-owned participant identity: BC sends this for bs/seminars. */
  source_line_id?: string
  /**
   * Stream-owned participant identity for mice/internal, where there is no BC
   * line to key off. REQUIRED by the backend on those streams — a pickup
   * without it is refused with `PARTICIPANT_IDENTITY_REQUIRED`. The UI mints it
   * before the first create and replays it unchanged on retry so a lost
   * response can't book a second room. Bulk pickups send one value; the backend
   * derives the per-room ids.
   */
  participant_external_id?: string
  /**
   * What the participant books: hotel | dayguest | other_hotel | no_services |
   * online | internal_guest | external_guest. Replaces the deprecated
   * is_dayguest / is_external / is_online request flags (not sent any more).
   */
  participant_type?: StoredParticipantType
  is_lecturer?: boolean
  booker_id?: string
  guest_party_id?: string
  booker_party_id?: string
  company_party_id?: string
  // Invoice routing — independent from guest/booker/company.
  invoice_owner_party_id?: string
  // Pin a specific room. UUID wins over name; an unknown room_number is a hard error.
  assigned_resource_id?: string
  room_number?: string
  /** true = fail the pickup if no room could be pinned, rather than booking roomless. */
  require_room_assignment?: boolean
  // Bulk pickup: backend loops the same payload N times. Defaults to 1.
  rooms_count?: number
  // Custom price override applied via MEWS reservations/update after create.
  price_overwrite?: {
    amount: number | number[]
    reason?: string
    currency?: string
    tax_codes?: string[]
  }
  // Rules engine: when supplied AND the resulting reservation is confirmed +
  // has a room assigned, pickup auto-fires rules + pushes new event_products
  // to MEWS. rate_id is the rates(id) FK — V68 replaced the old price_group
  // text key. Required for hotel pickups. Idempotent across repeats.
  rate_id?: number
  price_list_name?: string | null
}

/** Every hotel reservation is detached; `-linked` means it shares a linked-stay chain. */
export type ReservationType = 'hotel-detached' | 'hotel-detached-linked' | 'online'

export interface PickupReservationResponse {
  // 'no_availability' arrives on a 200 with warnings:['NO_AVAILABILITY'] — it is
  // NOT an error and NOT a success. No room was booked. Callers must check it
  // explicitly or they will report a green toast on a booking that didn't happen.
  status: 'created' | 'updated' | 'no_availability'
  type?: ReservationType
  warnings?: string[]
  reservation_id?: string
  reservation_number?: string
  db_reservation_id?: number
  event_id?: number
  assigned_resource_id?: string | null
  assigned_room_type?: 'comfort' | 'economy' | null
  requested_room_type?: 'comfort' | 'economy' | null
  assigned_category_name?: string | null
  /** true = the requested category was full and another was used. */
  category_fallback?: boolean
  tried_categories?: string[]
  rule_warning?: string
  // Bulk shape (rooms_count > 1)
  requested?: number
  created?: number
  updated?: number
  unavailable?: number
  failed?: number
  reservations?: any[]
  [key: string]: any
}

export interface UpdateParticipantPayload {
  reservation_id: number
  start_date?: string
  end_date?: string
  adult_count?: number
  child_count?: number
  guest_name?: string
  email?: string
  contact_no?: string
  notes?: string
  // Guest swap (EH-0309): the new guest's party row AND MEWS customer. The
  // backend swaps the customer on the MEWS reservation and stores the party —
  // the link every charge and bill follows. Send both, together.
  guest_party_id?: string
  mews_customer_id?: string
  room_type_code?: string
  // EZ/DZ axis for a category swap, orthogonal to the tier in room_type_code.
  // The backend resolves the target MEWS category from tier + occupancy
  // (single→Einzelzimmer, double→Doppelzimmer). Omitting it makes the backend
  // default single-first, so the edit path MUST send it for a DZ pick to apply
  // (EH-0372).
  occupancy?: 'single' | 'double'
  room_preference?: string
  type_of_stay?: string
  pre_evening_arrival?: boolean
  payment_method?: string
  company_no?: string
  company_name?: string
  source_line_id?: string
  is_lecturer?: boolean
  // @deprecated Do NOT send these. Every reservation is created standalone
  // already, so there is nothing to detach. The backend auto-heals any surviving
  // legacy block-bound row on the next update. Kept only so an old caller that
  // still sends them typechecks; they are a no-op on a modern reservation.
  detach_from_block?: boolean
  detach_rate_id?: string
  // Specific room re-assignment (UUID wins over name)
  assigned_resource_id?: string
  room_number?: string
  // Owner of the bill (independent from guest/booker/company)
  invoice_owner_party_id?: string
  arrival_time_of_day?: string | null
  departure_time_of_day?: string | null
  // Per-night MEWS price override
  price_overwrite?: {
    amount: number | number[]
    reason?: string
    currency?: string
    tax_codes?: string[]
  }
  // Same rules-engine fields as PickupReservationPayload. When the update
  // results in a confirmed + roomed reservation, rules auto-fire idempotently.
  rate_id?: number
  price_list_name?: string | null
}

// ─── Rules engine ────────────────────────────────────────────────────────
export type RuleTriggerKey =
  | 'arrival_morning'
  | 'arrival_afternoon'
  | 'departure_morning'
  | 'departure_afternoon'

export interface CloneRulePayload {
  new_name: string
  created_by?: string
}

export interface CloneRulePriceListPayload {
  from: string | null
  to: string
  created_by?: string
}

export interface ExecuteRulePayload {
  reservation_id: number
  trigger_key: RuleTriggerKey
  rate_id: number
  list_name?: string | null
  as_of?: string
}

export interface ExecuteRuleResponse {
  status: 'processed' | 'skipped' | 'failed' | 'partial'
  rules_matched: number
  rules_processed: number
  rules_failed: number
  event_product_ids: number[]
  errors: Array<{ rule_id: number; message: string }>
}

export interface DeleteParticipantPayload {
  reservation_id: number
  cancel_reason?: string | null
  send_cancellation_email?: boolean
  charge_cancellation_fee?: boolean
}

/** Storno (tier %) or no-show (100 %) product fee — backend stornoCharge.ts. */
export type ProductFeeKind = 'storno' | 'no_show'

export interface ProductFeeBucket {
  setting_key: string
  base: number
  percent: number
  amount: number
  currency: string
  lines: Array<{ table: 'event_products' | 'event_packages'; id: number; name: string; gross: number }>
  product: { id: number; name: string; tax_code: string | null } | null
}

/** GET /api/events/participants/:id/fee-preview — what the fee would charge. */
export interface ProductFeePlan {
  kind: ProductFeeKind
  reservation_id: number
  /** The storno tier in force now; null = before every tier (free) or no rule for the rate. */
  tier: { name: string | null; percent: number; applies_from: string } | null
  percent: number
  buckets: ProductFeeBucket[]
  unreadable: Array<{ table: string; id: number; reason: string }>
  missing_settings: string[]
  already_charged: string[]
}

export interface CancelWithFeePayload {
  kind: ProductFeeKind
  cancel_reason?: string | null
  send_cancellation_email?: boolean
  charge_cancellation_fee?: boolean
}

/** One folio line as the reception board renders it. */
export interface FolioItem {
  id: string
  name: string
  type: string | null
  unitCount: number
  grossValue: number
  netValue: number
  currency: string
  taxCodes: string[]
  accountingCategoryId: string | null
  consumedUtc: string | null
  canceled: boolean
  /**
   * Our catalogue name when the line could be attributed to an EventHub
   * charge, MEWS' own BillingName otherwise. Always populated — render this
   * rather than `name`.
   */
  displayName?: string
  /** Present only when the line was attributed unambiguously. */
  source?: {
    eventProductId: number
    eventId: number | null
    productName: string | null
    packageName: string | null
    sourceType: string | null
  }
}

/**
 * A MEWS bill plus its lines. `number` stays null until the bill closes —
 * MEWS assigns it from the fiscal counter — and `printable` mirrors MEWS' own
 * "Bill must be closed" rule so the UI can hide a print button that would only
 * return an error.
 */
export interface Folio {
  billId: string
  state: string
  type: string | null
  number: string | null
  customerId: string | null
  accountId: string | null
  issuedUtc: string | null
  closedUtc: string | null
  currency: string
  items: FolioItem[]
  totalGross: number
  /** Settled (State='Charged') payments on this bill. Present only when payments
   *  were fetched (reservation-scoped folios); undefined ≠ zero paid. */
  paid?: number
  /** totalGross − paid: what is still owed. Undefined when paid is. */
  balance?: number
  printable: boolean
}

export interface CheckInParticipantPayload {
  reservation_id: number
}

export interface CheckInEventParticipantsPayload {
  event_id?: number
  external_id?: string
}

// Single-row check-in. status='already_checked_in' is a non-error outcome:
// MEWS isn't called and the row keeps its previous last_action_at.
export interface CheckInParticipantResponse {
  status: 'checked_in' | 'already_checked_in'
  reservation_id?: number
  external_reservation_id?: string
  guest_name?: string
}

// Bulk check-in. The backend runs one MEWS call for all eligible rows then
// stamps each row only if MEWS confirmed its id in `started_ids`. A partial
// MEWS response surfaces here as status='partial' with details in `failed`.
export interface CheckInEventParticipantsResponse {
  status: 'ok' | 'partial' | 'noop'
  event_id?: number
  checked_in: Array<{ reservation_id: number; external_reservation_id: string; guest_name?: string }>
  skipped: Array<{ reservation_id: number; reason: string; guest_name?: string }>
  failed: Array<{ reservation_id: number; reason: string; guest_name?: string }>
}

export interface CreateAvailabilityBlockPayload {
  event_id: number
  name: string
  service_id: string
  rate_id: string
  booker_id: string
  resource_category_id: string
  arrival_date: string
  departure_date: string
  released_utc: string
  status: 'definite' | 'optional'
  pax_count: number
}

export interface UpdateServiceAvailabilityPayload {
  service_id: string
  resource_category_id: string
  availability_block_id: string
  arrival_date: string
  departure_date: string
  unit_count_adjustment: number
}

// Mirrors every documented MEWS `customers/add` attribute. Backend
// forwards each present field straight through to MEWS; absent fields
// are omitted so MEWS applies its own defaults.
export interface MewsAddressPayload {
  line1?: string
  line2?: string
  city?: string
  // camelCase to match the backend `mewsService` Address sub-object —
  // the route layer forwards `address` straight through without
  // normalisation, so any mismatch silently drops fields.
  postalCode?: string
  countryCode?: string
  stateCode?: string
}

export interface MewsIdentityCardPayload {
  number?: string
  type?: string
  issuance?: string   // YYYY-MM-DD
  expiration?: string // YYYY-MM-DD
}

export interface CreateMewsCustomerPayload {
  last_name: string
  first_name?: string
  second_last_name?: string
  title?: string              // Mister | Misses | Miss
  sex?: string                // Male | Female | Other
  birth_date?: string         // YYYY-MM-DD
  nationality_code?: string   // ISO-3166-1 alpha-2
  language_code?: string      // ISO-639-1
  email?: string
  phone?: string
  address?: MewsAddressPayload
  loyalty_code?: string
  tax_identifier?: string
  billing_code?: string
  identity_card?: MewsIdentityCardPayload
  send_marketing_emails?: boolean
  send_bills_and_documents_by_email?: boolean
  /** Accounts-receivable / Debitor → MEWS customers/add Options "Invoiceable". */
  invoiceable?: boolean
  notes?: string
}

// Mirrors every documented MEWS `companies/add` attribute. Same
// passthrough policy as the customer payload.
export interface CreateMewsCompanyPayload {
  name: string
  legal_name?: string
  contractual_name?: string
  contact_person?: string
  email?: string
  telephone?: string
  contact_person_email?: string
  contact_person_telephone?: string
  address?: MewsAddressPayload
  tax_identifier?: string
  additional_tax_identifier?: string
  billing_code?: string
  accounting_code?: string
  iata?: string
  degree_of_trust?: string
  notes?: string
  // Legacy alias kept for backward compatibility — backend treats it
  // as email when `email` itself is empty.
  contact?: string
}

export interface FindOrCreatePartyPayload {
  mews_id: string
  type: 'person' | 'company'
  name: string
  bc_id?: string | null
  email?: string | null
}

export interface FindOrCreatePartyResponse {
  status: 'found' | 'created'
  party_id: string
  mews_id: string
  created: boolean
}

interface ParticipantResponse {
  status: string
  /** updateParticipant: MEWS took the new customer on the reservation. */
  customer_swapped?: boolean
  /** updateParticipant: the MEWS customer now on the reservation. */
  new_mews_customer_id?: string
  [key: string]: any
}

export interface SystemCheckItem {
  name: string
  status: 'pass' | 'warn' | 'fail'
  message: string
}

export interface SystemCheckResponse {
  status: string
  summary: {
    total: number
    passed: number
    failed: number
    warned: number
  }
  checks: SystemCheckItem[]
  timestamp: string
}

export interface AiChatMessage {
  role: 'user' | 'assistant' | 'system'
  content: string
}

export interface AiChatRequest {
  messages: AiChatMessage[]
  property_id: number
}

export interface FileUploadUrlRequest {
  property_id: number
  entity_type: string
  entity_id: number | string
  file_name: string
  mime_type: string
}

export interface FileUploadUrlResponse {
  upload_url: string
  storage_key: string
  expires_in: number
}

export interface FileRegisterRequest {
  property_id: number
  file_name: string
  storage_key: string
  mime_type: string
  size_bytes: number
  uploaded_by: string
  entity_type?: string
  entity_id?: number | string
  tag?: string
}

export interface FileRecord {
  id: number
  property_id: number
  file_name: string
  storage_key: string
  mime_type: string
  size_bytes: number
  uploaded_by: string
  tag?: string
  created_at?: string
}

export interface FileAssignment {
  id: number
  file_id: number
  entity_type: string
  entity_id: number | string
  tag?: string
}

export interface FileEntityResponse {
  count: number
  files: FileRecord[]
}

export interface FileDownloadResponse {
  download_url: string
  file_name: string
  mime_type: string
  expires_in: number
}

// Query params for the proxy upload route (POST /api/files/upload). Mirrors the
// old getFileUploadUrl + registerFile pair, but the backend uploads to Blob
// Storage itself and registers/assigns in one call — the file bytes travel in
// the request body, everything else as query params.
export interface FileProxyUploadParams {
  property_id: number
  entity_type: string
  entity_id: number | string
  file_name: string
  mime_type: string
  uploaded_by?: string
  tag?: string
}

export interface AiChatResponse {
  response: string
  usage?: {
    prompt_tokens: number
    completion_tokens: number
    total_tokens: number
  }
}

export interface SystemStatusResponse {
  backend: {
    name: string
    version: string
    gitSha: string
    environment: string
    uptime: string
    nodeVersion: string
    status: string
  }
  database: {
    status: string
    version: string
    host: string
    tables: number
    flywayVersion: string
    latencyMs: number
    ssl: boolean
  }
}

/** Per-entity slice of the worker's most recent tick. */
export interface MewsSyncWorkerEntitySlice {
  processed: number
  succeeded: number
}

/**
 * Snapshot of the MEWS sync worker (see backend `mewsSyncWorker.ts`).
 * `worker === null` means the process never instantiated one — either
 * MEWS_SYNC_WORKER_ENABLED=false at startup, or a test process. The UI
 * should render that as "worker not active" rather than as red/error.
 */
export interface MewsSyncWorkerStats {
  enabled: boolean
  running: boolean
  is_ticking: boolean
  tick_interval_ms: number
  last_tick_at: string | null
  last_tick_ms: number | null
  last_tick_processed: number
  last_tick_succeeded: number
  last_tick_by_entity_type: Record<string, MewsSyncWorkerEntitySlice>
  tick_count: number
  /**
   * red    — worker not running (disabled or stopped)
   * yellow — running but hasn't ticked recently (drifting)
   * green  — ticked within the last 2× tick interval
   */
  health: 'red' | 'yellow' | 'green'
}

/**
 * Optional background worker that keeps MEWS room availability in a
 * property/category/day snapshot. It is deliberately independent from the
 * MEWS write-sync worker: deployments that have not opted in do not return
 * this object, and the Administration UI leaves the snapshot section hidden.
 */
export interface AvailabilitySnapshotWorkerStats {
  enabled: boolean
  running?: boolean
  is_ticking?: boolean
  tick_interval_ms?: number
  horizon_days?: number
  max_age_ms?: number
  last_tick_at?: string | null
  last_tick_ms?: number | null
  last_tick_processed?: number
  last_tick_succeeded?: number
  tick_count?: number
  health: 'red' | 'yellow' | 'green'
}

export interface MewsSyncStatusResponse {
  worker: MewsSyncWorkerStats | null
  availability_snapshot_worker?: AvailabilitySnapshotWorkerStats | null
  by_outcome: Record<string, number>
  by_entity_type: Record<string, Record<string, number>>
  stuck_pending: Array<Record<string, any>>
  recent_permanent: Array<Record<string, any>>
  terminal_soft: Array<Record<string, any>>
  timestamp: string
}

class BackendApiService {
  private baseUrl: string

  constructor() {
    this.baseUrl = BACKEND_API_URL
  }

  /**
   * Default upper bound for a single backend call. MEWS-touching routes
   * (reservation retry, event upsert, finance sync) can take 30s+ on a
   * cold call, so we give them headroom — but without a cap the fetch
   * silently spins forever when the backend or MEWS hangs, which the UI
   * sees as a frozen button (the `finally` block never runs). 120s is
   * "long enough for normal MEWS round-trips, short enough that the
   * operator sees an error instead of an indefinite spinner."
   *
   * Override per-call by passing `timeoutMs` in the options. Pass `0`
   * to disable entirely (avoid unless you know the route streams).
   */
  private static readonly DEFAULT_TIMEOUT_MS = 120_000

  async request<T>(
    endpoint: string,
    options: RequestInit & { timeoutMs?: number } = {},
  ): Promise<T> {
    const { timeoutMs, ...fetchOptions } = options
    const url = `${this.baseUrl}${endpoint}`
    // Pre-session auth endpoints authenticate from the body and must not
    // ask for a JWT at all — see PRE_SESSION_ROUTES. For every other route:
    // `getUpstreamJwt()` returns null when no session exists yet and we
    // simply omit Authorization; routes that require auth will 401, which
    // is the right error for those flows.
    const token = PRE_SESSION_ROUTES.has(endpoint) ? null : await getUpstreamJwt()

    // Compose abort signal: caller-supplied + our internal timeout.
    // AbortSignal.any merges multiple signals so cancellation flows
    // from whichever fires first; falls back to a single signal in
    // browsers that don't yet support .any (shouldn't happen in our
    // matrix but harmless).
    const effectiveTimeout = timeoutMs ?? BackendApiService.DEFAULT_TIMEOUT_MS
    const timeoutController = effectiveTimeout > 0 ? new AbortController() : null
    const timeoutHandle = timeoutController
      ? setTimeout(() => timeoutController.abort(), effectiveTimeout)
      : null
    const signals: AbortSignal[] = []
    if (fetchOptions.signal) signals.push(fetchOptions.signal)
    if (timeoutController) signals.push(timeoutController.signal)
    const composedSignal = signals.length === 0
      ? undefined
      : signals.length === 1
        ? signals[0]
        : (AbortSignal as any).any?.(signals) ?? signals[0]

    let response: Response
    try {
      response = await fetch(url, {
        ...fetchOptions,
        signal: composedSignal,
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...fetchOptions.headers,
        },
      })
    } catch (err: any) {
      // AbortError on our timeout signal: surface a friendly message.
      // AbortError on the caller's signal: bubble up unchanged so they
      // can distinguish a deliberate cancel from a slow backend.
      if (err?.name === 'AbortError' && timeoutController?.signal.aborted) {
        throw new BackendApiError(
          `Backend request timed out after ${effectiveTimeout / 1000}s. The server may be busy — please retry.`,
          0,
          'TIMEOUT',
        )
      }
      throw err
    } finally {
      if (timeoutHandle) clearTimeout(timeoutHandle)
    }

    if (!response.ok) {
      const errorBody = await response.text()
      let errorMessage = `Backend API error: ${response.status}`
      let code: string | undefined
      let details: any
      let failureCode: string | undefined
      try {
        const parsed = JSON.parse(errorBody)
        errorMessage = parsed.error || parsed.message || errorMessage
        code = parsed.code
        details = parsed.details
        // Pickup refusals carry a classified cause alongside the generic code
        // (detachedHotelReservation.ts returns both). Keep it so callers can
        // tell "MEWS restriction on this rate/category" from "rate deleted"
        // from "MEWS timed out" — all three arrive as AUTO_DETACH_FAILED.
        failureCode = parsed.failure_code
        // Some endpoints answer a failed operation with the OPERATION RESULT
        // rather than an { error } envelope — `POST /events/chargePackage`
        // returns 400 carrying { status:'error', errors:[…] }
        // (eventRoutes.ts: `result.status === 'error' ? 400 : 200`). That body
        // has neither `error` nor `message`, so the real MEWS reasons were
        // dropped and the operator saw a bare "Backend API error: 400".
        //
        // NOTE: chargePackage carries NO charged/canceled/skipped on any of its
        // return paths — an earlier version of this comment claimed it did, and
        // that assumption is what let a successful package charge report
        // "Keine Änderungen an MEWS notwendig". See utils/chargeResultShape.ts.
        // Fall back to the plural `errors` array and keep it in details so
        // callers can render the individual lines.
        if (!parsed.error && !parsed.message && Array.isArray(parsed.errors) && parsed.errors.length > 0) {
          errorMessage = parsed.errors.join(' | ')
          if (details === undefined) details = { errors: parsed.errors }
        }
      } catch {
        if (errorBody) errorMessage = `Backend API error: ${response.status} - ${errorBody}`
      }
      throw new BackendApiError(errorMessage, response.status, code, details, failureCode)
    }

    const text = await response.text()
    return text ? JSON.parse(text) : ({} as T)
  }

  async upsertMiceEvent(data: UpsertEventPayload): Promise<BackendEventResponse> {
    const payload = {
      ...data,
      property_id: data.property_id ?? PROPERTY_ID,
    }
    return this.request<BackendEventResponse>('/api/events/upsertEvent/mice', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  }

  async upsertInternalEvent(data: UpsertEventPayload): Promise<BackendEventResponse> {
    const payload = {
      ...data,
      property_id: data.property_id ?? PROPERTY_ID,
    }
    return this.request<BackendEventResponse>('/api/events/upsertEvent/internal', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  }

  /**
   * Upsert against the correct stream endpoint. The backend exposes one per
   * stream (/upsertEvent/{mice,internal,seminars,bs}) and each FORCES
   * event_stream to its own value — so routing a seminars/bs event to the mice
   * endpoint makes the backend stamp it "mice" and then reject it with
   * EVENT_IDENTITY_MISMATCH ("belongs to stream seminars, not mice"). Always
   * route by the event's real stream. Unknown streams fall back to mice.
   */
  async upsertEventForStream(stream: string | null | undefined, data: UpsertEventPayload): Promise<BackendEventResponse> {
    const s = String(stream || '').trim().toLowerCase()
    const endpoint = (s === 'internal' || s === 'seminars' || s === 'bs') ? s : 'mice'
    const payload = { ...data, property_id: data.property_id ?? PROPERTY_ID }
    return this.request<BackendEventResponse>(`/api/events/upsertEvent/${endpoint}`, {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  }

  async upsertEvent(data: UpsertEventPayload): Promise<BackendEventResponse> {
    return this.upsertEventForStream(data.event_stream, data)
  }

  // ── Settings registry (admin) ───────────────────────────────────────────
  /**
   * Every setting the backend DECLARES, with its resolved value and where that
   * value came from. Includes settings nothing has ever set — that is the
   * point: a screen built from stored rows alone cannot distinguish "not
   * configured" from "does not exist".
   */
  async getSettings(propertyId: number): Promise<SettingsCatalogue> {
    return this.request<SettingsCatalogue>(`/api/system/settings?property_id=${propertyId}`)
  }

  /** Store one declared setting. `value: null` clears it (back to env/default). */
  async putSetting(key: string, value: string | null, propertyId: number): Promise<{
    ok: boolean; key: string; scope: 'system' | 'property'; live: boolean; updated_by: string | null
  }> {
    return this.request(`/api/system/settings/${encodeURIComponent(key)}`, {
      method: 'PUT',
      body: JSON.stringify({ value, property_id: propertyId }),
    })
  }

  // ── Background workers (admin) ──────────────────────────────────────────
  /**
   * Every declared worker with its settings, live health, last recorded run
   * and 24-hour tally. `live` is this container's view; `last_run` is durable.
   */
  async getWorkers(propertyId: number): Promise<WorkersResponse> {
    return this.request<WorkersResponse>(`/api/system/workers?property_id=${propertyId}`)
  }

  /** One worker's recent runs, newest first, from worker_runs. */
  async getWorkerRuns(name: string, limit = 20): Promise<{ worker: string; runs: WorkerRunRow[] }> {
    return this.request(`/api/system/workers/${encodeURIComponent(name)}/runs?limit=${limit}`)
  }

  // ── Q2: per-user event-review tracking + in-app notifications ───────────
  /** The current user's review state for one event (incl. a `stale` flag). */
  async getEventAcknowledgement(eventId: number): Promise<EventAckState> {
    return this.request<EventAckState>(`/api/events/${eventId}/acknowledgement`)
  }

  /**
   * List-wide "seen" roll-up for the current user: how many events are unseen,
   * seen (current version) or changed (seen an older version). Optionally scoped
   * to one property. Powers the events-list "Gesehen" KPI.
   */
  /**
   * `range` scopes the roll-up to the period the events list is showing. The
   * backend applies the list's own rule — the event's START day inside the
   * window — so the KPI and the rows below it always count the same events.
   */
  /**
   * Per-day meeting-space availability (venue days) — the backend source of
   * truth for the availability page and the Räume strip (D200). Same rules as
   * /check-availability: confirmed-only holds, transition buffer, combined rooms;
   * suggested bookings come back as contested. Max 100 days per call.
   */
  async getSpaceAvailabilityCalendar(propertyId: number, from: string, to: string): Promise<{
    property_id: number
    from: string
    to: string
    total_spaces: number
    space_ids: number[]
    days: Array<{ date: string; held_space_ids: number[]; contested_space_ids: number[]; suggested_counts: Record<string, number> }>
  }> {
    const q = new URLSearchParams({ property_id: String(propertyId), from, to })
    return this.request(`/api/space-bookings/availability-calendar?${q.toString()}`)
  }

  /**
   * Storno rules per rate, live from the PMS for one stay window.
   *
   * Deliberately not a sync: MEWS resolves a cancellation policy for a RATE and
   * a WINDOW (`cancellationPolicies/getByRates`), so there is no catalogue to
   * mirror and a stored copy would be true only for the dates it was read with.
   */
  async getCancellationPolicies(
    propertyId: number,
    range: { from?: string; to?: string } = {},
  ): Promise<{
    property_id: number
    window: { start: string; end: string }
    status: 'ok' | 'unavailable'
    warning: { code: string; message: string } | null
    rates: Array<{
      rate_id: number
      rate_name: string
      mews_rate_id: string | null
      status: 'ok' | 'unmapped'
      policies: any[]
    }>
  }> {
    const q = new URLSearchParams({ property_id: String(propertyId) })
    if (range.from) q.set('from', range.from)
    if (range.to) q.set('to', range.to)
    return this.request(`/api/pms/cancellation-policies?${q.toString()}`)
  }

  /**
   * What the PMS holds as CHARGED on the given days, split by who charged it.
   *
   * The F&B board's 360° source. `origin` is the whole point:
   *   'mews'      a catalogue product order — quantity is trustworthy
   *   'eventhub'  our own packages/rules, seen from the provider side; the
   *               covers already come from event_products, so `quantity` is null
   *   'external'  POS / reception / till — money exact, quantity unknowable
   *
   * `quantity_source` says why a quantity is null, so the UI renders a dash
   * rather than a zero. Read-only.
   */
  async getPmsChargedItems(params: { date: string; days?: number; serviceIds?: string[] }): Promise<{
    date: string
    days: number
    count: number
    by_origin: Record<string, { rows: number; quantity: number | null; gross: number }>
    excluded: Record<string, { items: number; net: number; gross: number; currency: string }>
    rows: Array<{
      date: string
      origin: 'mews' | 'eventhub' | 'external'
      productId: string | null
      name: string
      serviceName: string | null
      quantity: number | null
      quantitySource: 'unitCount' | 'ours' | 'unknown'
      net: number
      gross: number
      currency: string
      items: number
    }>
  }> {
    const q = new URLSearchParams({ date: params.date })
    if (params.days) q.set('days', String(params.days))
    if (params.serviceIds?.length) q.set('service_ids', params.serviceIds.join(','))
    return this.request(`/api/pms/charged-items?${q.toString()}`)
  }

  /**
   * What MEWS holds as the reservation note for each participant of an event,
   * plus the snapshot EventHub would write now. Read-only.
   */
  async getReservationNotes(eventId: number): Promise<{
    event_id: number
    auto_sync: boolean
    reservations: Array<{
      reservation_id: number
      external_reservation_id: string
      current_snapshot: string
      in_sync: boolean
      pms_notes: Array<{ id: string; text: string; type: string | null; created_utc: string | null }> | null
      pms_error?: string
    }>
  }> {
    return this.request(`/api/events/${eventId}/reservation-notes`)
  }

  /** Push the current note snapshot to MEWS for this event. Operator action. */
  async resyncReservationNotes(eventId: number): Promise<{
    event_id: number; checked: number; pushed: number; unchanged: number; failed: number
  }> {
    return this.request(`/api/events/${eventId}/reservation-notes/resync`, { method: 'POST' })
  }

  /**
   * Pin one space booking as the event's Hauptraum.
   *
   * Through the backend, not PostgREST, because pinning is more than a column
   * write: the previous pin has to be cleared in the SAME transaction (at most
   * one per event), and every MEWS reservation note for the event has to be
   * re-stamped — the note advertises "Raum: …" to reception, so a changed
   * Hauptraum that never reaches MEWS leaves the desk reading the old room.
   */
  async setMainSpaceBooking(bookingId: number): Promise<{ status: string; booking: any }> {
    return this.request(`/api/space-bookings/${bookingId}`, {
      method: 'PUT',
      body: JSON.stringify({ is_main: true }),
    })
  }

  /**
   * The mirrored Storno rules for the Storno tab: the named catalogue, and per
   * rate group its tiers, its rates and a deadline with its clock time.
   */
  async getStornoRules(propertyId: number): Promise<StornoRulesResponse> {
    return this.request(`/api/pms/cancellation-policies/rules?property_id=${encodeURIComponent(String(propertyId))}`)
  }

  /** Pull the MEWS Storno rules (catalogue + per rate group) into EventHub's mirror. */
  async syncCancellationPolicies(propertyId: number): Promise<{
    property_id: number; fetched: number; inserted: number; updated: number; deactivated: number
    rate_groups?: { fetched: number; inserted: number; updated: number; deactivated: number }
  }> {
    // The property goes in the BODY: pmsRoutes' propertyIdOf reads req.query
    // only for GET, so a query-string id on this POST came through as undefined
    // ("property_id must be a positive integer").
    return this.request('/api/pms/cancellation-policies/sync', {
      method: 'POST',
      body: JSON.stringify({ property_id: propertyId }),
    })
  }

  async getAcknowledgementSummary(
    propertyId?: number,
    range?: { from?: string | null; to?: string | null },
  ): Promise<{ unseen: number; seen: number; changed: number; total: number }> {
    const q = new URLSearchParams()
    if (propertyId != null) q.set('property_id', String(propertyId))
    if (range?.from) q.set('from', range.from)
    if (range?.to) q.set('to', range.to)
    const qs = q.toString()
    return this.request(`/api/events/acknowledgements/summary${qs ? `?${qs}` : ''}`)
  }

  /** Mark the event's current version as reviewed by the current user. */
  async acknowledgeEvent(eventId: number, printed = false): Promise<EventAckState> {
    return this.request<EventAckState>(`/api/events/${eventId}/acknowledge`, {
      method: 'POST',
      body: JSON.stringify({ printed }),
    })
  }

  /** Undo the current user's review — the event reads as un-seen again. */
  async unacknowledgeEvent(eventId: number): Promise<EventAckState> {
    return this.request<EventAckState>(`/api/events/${eventId}/acknowledge`, {
      method: 'DELETE',
    })
  }

  // ── Function-sheet "checked" lock (global, shared — separate from acks) ──
  /** The event's global function-sheet check state. */
  /** Event "gültig" state — readable by everyone signed in. */
  async getEventValid(eventId: number): Promise<EventValidState> {
    return this.request<EventValidState>(`/api/events/${eventId}/valid`)
  }

  /** Mark the event "gültig" (needs `events:set-valid:<its stream>`, V233). */
  async setEventValid(eventId: number): Promise<EventValidState> {
    return this.request<EventValidState>(`/api/events/${eventId}/valid`, { method: 'POST' })
  }

  /** "Gültig" state of every session of an event (V203). */
  async getSessionValidity(eventId: number): Promise<SessionValidState[]> {
    return this.request<SessionValidState[]>(`/api/events/${eventId}/sessions/valid`)
  }

  /** Mark one session (day-row) "gültig" (needs the event stream's sign-off right). */
  async setSessionValid(eventId: number, sessionId: number): Promise<SessionValidState> {
    return this.request<SessionValidState>(`/api/events/${eventId}/sessions/${sessionId}/valid`, { method: 'POST' })
  }

  /** Withdraw one session's "gültig" (needs the event stream's sign-off right). */
  async unsetSessionValid(eventId: number, sessionId: number): Promise<SessionValidState> {
    return this.request<SessionValidState>(`/api/events/${eventId}/sessions/${sessionId}/valid`, { method: 'DELETE' })
  }

  /** Clear "gültig" again (needs `events:set-valid:<its stream>`, V233). */
  async unsetEventValid(eventId: number): Promise<EventValidState> {
    return this.request<EventValidState>(`/api/events/${eventId}/valid`, { method: 'DELETE' })
  }

  async getFunctionSheetCheck(eventId: number): Promise<FunctionSheetCheckState> {
    return this.request<FunctionSheetCheckState>(`/api/events/${eventId}/function-sheet/check`)
  }

  /** Mark the function sheet as checked (locks the event for everyone). */
  async checkFunctionSheet(eventId: number): Promise<FunctionSheetCheckState> {
    return this.request<FunctionSheetCheckState>(`/api/events/${eventId}/function-sheet/check`, { method: 'POST' })
  }

  /** Clear the check, unlocking the event again. */
  async uncheckFunctionSheet(eventId: number): Promise<FunctionSheetCheckState> {
    return this.request<FunctionSheetCheckState>(`/api/events/${eventId}/function-sheet/check`, { method: 'DELETE' })
  }

  async getNotifications(opts: { unread?: boolean; limit?: number } = {}): Promise<{ notifications: AppNotification[] }> {
    const q = new URLSearchParams()
    if (opts.unread) q.set('unread', '1')
    if (opts.limit) q.set('limit', String(opts.limit))
    const qs = q.toString()
    return this.request<{ notifications: AppNotification[] }>(`/api/notifications${qs ? `?${qs}` : ''}`)
  }

  async getUnreadNotificationCount(): Promise<number> {
    const r = await this.request<{ count: number }>('/api/notifications/unread-count')
    return r.count ?? 0
  }

  async markNotificationRead(id: number): Promise<void> {
    await this.request(`/api/notifications/${id}/read`, { method: 'POST' })
  }

  async markAllNotificationsRead(): Promise<void> {
    await this.request('/api/notifications/read-all', { method: 'POST' })
  }

  // Per-day, per-category availability for a date range. The backend prefers a
  // fresh, complete local snapshot and falls back to MEWS as needed. It returns
  // the same net inventory used by event-upsert preflight, keyed by
  // mews_category_id so callers can map directly to category UUIDs in the UI.
  async getAvailabilityCalendar(opts: {
    from: string
    to: string
    property_id?: number
    pre_evening_arrival?: boolean
    // Opt-in compatibility alias of availability_summary.total.per_day.
    with_net?: boolean
  }): Promise<{
    property_id: number
    from: string
    to: string
    room_types: Array<{
      room_type_id: number
      mews_category_id: string
      name: string
      tier: string | null
      occupancy: string | null
      per_day: Array<{
        date: string
        // Canonical availability: authoritative MEWS value while positive,
        // reconstructed signed value only below MEWS' zero floor.
        available: number
      }>
      bookable_per_day: Array<{ date: string; available: number }>
      net_per_day: Array<{ date: string; available: number }>
      // Breakdown behind `available`. Physical rooms in this category — flat,
      // not per-day. 0 when unresolvable.
      capacity: number
      // Rooms held by an event's AvailabilityBlock (no guest yet). A date is
      // OMITTED (not present in the array) when that day's component was never
      // computed — never treat a missing date as 0.
      blocked_per_day: Array<{ date: string; count: number }>
      // Rooms taken by an actual, non-block-attached MEWS reservation. Same
      // "missing date = not computed, not zero" rule as blocked_per_day.
      reserved_per_day: Array<{ date: string; count: number }>
      // Subset of reserved_per_day with NO matching pms_reservations row — a
      // MEWS reservation EventHub's own tables never recorded (booked directly
      // in MEWS Operations, another channel, or an EventHub reception walk-in,
      // which is deliberately never mirrored into pms_reservations either).
      // "Not in our ledger," not "a problem." Same missing-date rule.
      reserved_unknown_per_day: Array<{ date: string; count: number }>
    }>
    // Present only when with_net. Alias of availability_summary.total.per_day.
    property_net_per_day?: Array<{ date: string; net: number }>
    /** `snapshot` when served from a fresh complete availability snapshot; otherwise `live`. */
    source?: 'snapshot' | 'live'
    snapshot_fetched_at?: string | null
    /**
     * Backend range summary. `total.per_day` includes every active category,
     * including categories without an EventHub room-type mapping, and is the
     * authoritative property-wide number for the date picker.
     */
    availability_summary?: {
      total: {
        per_day: Array<{ date: string; available: number }>
        busiest_day?: { date: string; available: number } | null
      }
      room_types?: Array<{
        type: string
        available: number
        per_day?: Array<{ date: string; available: number }>
        busiest_day?: { date: string; available: number } | null
      }>
      room_categories?: Array<{
        name: string
        available: number
        per_day?: Array<{ date: string; available: number }>
        busiest_day?: { date: string; available: number } | null
      }>
    }
    error?: string
  }> {
    const q = new URLSearchParams({ from: opts.from, to: opts.to })
    q.set('property_id', String(opts.property_id ?? PROPERTY_ID))
    if (opts.pre_evening_arrival) q.set('pre_evening_arrival', 'true')
    if (opts.with_net) q.set('with_net', 'true')
    return this.request(`/api/events/availability?${q.toString()}`, { method: 'GET' })
  }

  async deleteEvent(eventId: string | number): Promise<DeleteEventResponse> {
    return this.request<DeleteEventResponse>(`/api/events/${eventId}`, {
      method: 'DELETE',
    })
  }

  // Mint a customer-portal capability token for an event or a single
  // reservation. Returns the RAW token once (unrecoverable) plus the backend's
  // portal_url; the token authorizes exactly that one entity. `scope` defaults
  // to 'read_write' server-side. Staff-only (requireJwt) — the FE additionally
  // gates the affordance behind the 'customer-portal' permission.
  async mintPortalGrant(
    entityType: 'event' | 'reservation',
    entityId: number,
    opts?: { scope?: 'read' | 'read_write'; label?: string; expires_in_days?: number },
  ): Promise<{ token: string; portal_url: string | null; scope: string; expires_at?: string | null }> {
    const path = entityType === 'event'
      ? `/api/events/${entityId}/portal-grant`
      : `/api/events/reservations/${entityId}/portal-grant`
    return this.request(path, { method: 'POST', body: JSON.stringify(opts ?? {}) })
  }

  // Soft-cancel an event through the atomic MEWS-teardown endpoint. Unlike
  // routing a cancel through upsertEvent (which releases the space but leaves
  // the room BLOCK held), this frees every MEWS/space/product hold. `opts`
  // carries the MICE status (cancelled | closed-lost) + cancellation reason,
  // persisted server-side into source_data.cancellation.
  async cancelEvent(eventId: string | number, opts?: CancelEventOptions): Promise<CancelEventResponse> {
    return this.request<CancelEventResponse>(`/api/events/${eventId}/cancel`, {
      method: 'POST',
      body: JSON.stringify(opts ?? {}),
    })
  }

  async pickupReservation(data: PickupReservationPayload): Promise<PickupReservationResponse> {
    return this.request<PickupReservationResponse>('/api/events/pickupReservation', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * The authoritative hotel readout for an event — block occupancy, off-block
   * rows, errors, totals. Prefer this over recomputing occupancy from the
   * reservation list: a reservation's `pms_block_id` / `mews_availability_block_id`
   * are NULL on every detached row, so any client-side "is it on a block?" check
   * built on them reports 0 without failing.
   */
  async getHotelSummary(eventId: number): Promise<HotelSummary> {
    return this.request<HotelSummary>(`/api/events/${eventId}/hotel-summary`)
  }

  /**
   * Full event detail. Unlike a raw PostgREST read of `pms_blocks`, the blocks
   * here carry the server-derived category identity (`mews_category_id`,
   * `room_type_tier`, `room_type_name`, `rate_name`) and `picked_up`, none of
   * which exist as columns.
   */
  async getEventDetail(eventId: number): Promise<EventDetail> {
    return this.request<EventDetail>(`/api/events/${eventId}`)
  }

  // ─── Rules engine ────────────────────────────────────────────────────
  // Plain CRUD on rules / rule_products / rule_product_triggers /
  // rule_product_prices / price_groups goes through PostgREST directly.
  // Only the orchestrated operations live here.
  async cloneRule(ruleId: number, data: CloneRulePayload): Promise<{ id: number; name: string }> {
    return this.request(`/api/rules/${ruleId}/clone`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async cloneRulePriceList(ruleId: number, data: CloneRulePriceListPayload): Promise<{ rows_created: number }> {
    return this.request(`/api/rules/${ruleId}/price-lists/clone`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async executeRule(data: ExecuteRulePayload): Promise<ExecuteRuleResponse> {
    return this.request('/api/rules/execute', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateParticipant(data: UpdateParticipantPayload): Promise<ParticipantResponse> {
    return this.request<ParticipantResponse>('/api/events/updateParticipant', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  // (updateReservationCustomer — a raw MEWS `reservations/updateCustomer` through
  // the generic proxy — is gone on purpose: it changed the guest in MEWS while
  // EventHub kept the old profile. A guest swap goes through updateParticipant
  // with guest_party_id + mews_customer_id, EH-0309.)

  async deleteParticipant(data: DeleteParticipantPayload): Promise<ParticipantResponse> {
    return this.request<ParticipantResponse>('/api/events/deleteParticipant', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /** What a storno / no-show product fee would charge this participant. */
  async getProductFeePreview(reservationId: number, kind: ProductFeeKind): Promise<ProductFeePlan> {
    return this.request<ProductFeePlan>(`/api/events/participants/${reservationId}/fee-preview?kind=${kind}`)
  }

  /**
   * Post the product fee, then cancel the participant. A fee that cannot be
   * posted (422 settings / unvalued line, 502 MEWS) leaves them uncancelled.
   */
  /**
   * No-show on OUR side only (V218): posts the no-show product fee, takes the
   * guest out of product charging and releases their original product orders.
   * The MEWS reservation is not touched. 207 = fee posted, release to retry.
   */
  async chargeNoShow(reservationId: number): Promise<{ status: 'charged' | 'release_failed'; message?: string; released?: number }> {
    return this.request(`/api/events/participants/${reservationId}/charge-no-show`, { method: 'POST', body: '{}' })
  }

  async cancelParticipantWithFee(reservationId: number, data: CancelWithFeePayload): Promise<ParticipantResponse> {
    return this.request<ParticipantResponse>(`/api/events/participants/${reservationId}/cancel-with-fee`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  // ── Reception (PMS board) ────────────────────────────────────────────
  // Backed by /api/reception/*, which requires a real user JWT plus the
  // `pms-board` permission — these are guest-facing operations, so the shared
  // API key is deliberately not sufficient on its own.

  /** Every folio for the guest on this reservation, lines included. */
  async getReservationFolios(reservationId: number): Promise<{
    status: string
    customer_id: string | null
    folios: Folio[]
    reason?: string
  }> {
    return this.request(`/api/reception/reservations/${reservationId}/folios`)
  }

  /** One folio with its lines. */
  async getBill(billId: string): Promise<{ status: string; folio: Folio }> {
    return this.request(`/api/reception/bills/${encodeURIComponent(billId)}`)
  }

  /**
   * Issue the invoice. Irreversible: MEWS assigns the number from its fiscal
   * counter and the bill can no longer take a payment, so settle first.
   */
  async closeBill(billId: string): Promise<{ status: 'closed' | 'already_closed'; bill_id: string }> {
    return this.request(`/api/reception/bills/${encodeURIComponent(billId)}/close`, { method: 'POST' })
  }

  /**
   * URL of the official MEWS invoice PDF. Only meaningful once the bill is
   * closed — the endpoint answers 409 BILL_NOT_CLOSED otherwise, which is why
   * callers should check `folio.printable` before opening it.
   */
  billPdfUrl(billId: string): string {
    return `${this.baseUrl}/api/reception/bills/${encodeURIComponent(billId)}/pdf`
  }

  /**
   * At-a-glance desk state: movements, room readiness, occupancy, open
   * balances on today's departures, and alerts.
   *
   * Partially degradable by design — `rooms.unavailable` and
   * `balances.partial` flag the pieces MEWS could not fully answer, so the UI
   * can say "unavailable" instead of showing a zero as fact.
   */
  async getReceptionOverview(propertyId: number, date: string): Promise<any> {
    return this.request(`/api/reception/overview?property_id=${propertyId}&date=${date}`)
  }

  /**
   * Search people AND companies for the 360 view. Backed by our local
   * `parties` table rather than MEWS: MEWS' getAll filters match exactly and
   * silently ignore unrecognised filter names, which in a search box would
   * quietly return everything.
   */
  async searchParties(q: string, type?: 'person' | 'company'): Promise<{
    status: string
    count: number
    results: Array<{
      id: string
      type: string
      name: string | null
      email: string | null
      mewsId: string | null
      reservationCount: number
    }>
  }> {
    const t = type ? `&type=${type}` : ''
    return this.request(`/api/reception/parties/search?q=${encodeURIComponent(q)}${t}`)
  }

  /** Full history, next/last stay, stats and bills for one person or company. */
  async getPartyProfile(partyId: string): Promise<any> {
    return this.request(`/api/reception/parties/${encodeURIComponent(partyId)}/profile`)
  }

  /** Rooms in a reservation's category with free/occupied + housekeeping state, for the assign-room picker. */
  /**
   * Rooms assignable to a reservation, scoped to ONE MEWS category.
   *
   * `categoryId` overrides which category is listed — an upgrade or a downgrade
   * when the booked category has nothing free. It changes only what is listed:
   * the reservation stays booked and priced at its own category, which is why
   * the response reports both ids and `is_other_category`.
   */
  async getAssignableRooms(reservationId: number, categoryId?: string | null): Promise<{
    status: string
    rooms: Array<{
      resourceId: string; name: string; state: string | null; occupied: boolean
      /** Free of guests, but its capacity is held by an availability block. */
      held_by_block?: boolean
    }>
    /** The listed category's name (e.g. "Economy Single Room"). */
    category: string | null
    /** MEWS id of the category actually listed. */
    category_id?: string | null
    /** MEWS id of the category the guest booked. */
    booked_category_id?: string | null
    /** True when the list is NOT the booked category. */
    is_other_category?: boolean
    /**
     * Rooms this category can still give out for the stay — MEWS' availability,
     * already net of blocks. null = not checked (own category, where a swap is
     * capacity-neutral) or the check could not run.
     */
    sellable?: number | null
    availability_reason?: string | null
    /**
     * categoryId → rooms still givable for this stay, for every category. Lets
     * the picker show "0 frei" in the dropdown instead of making the operator
     * switch into a category to discover it is sold out.
     */
    category_availability?: Record<string, number | null>
    assigned_resource_id: string | null
    reason?: string
  }> {
    const query = categoryId ? `?category_id=${encodeURIComponent(categoryId)}` : ''
    return this.request(`/api/reception/reservations/${reservationId}/assignable-rooms${query}`)
  }

  /**
   * Move a reservation to a different room category, optionally assigning a room
   * of that category in the same call — MEWS refuses a room outside the
   * reservation's own category, so the two travel together.
   *
   * This REPRICES the stay at the new category's current rate (product decision
   * 2026-09-14), which replaces a negotiated event price. Callers must say so
   * before the user commits.
   */
  /**
   * Move a reservation to another room category.
   * `reprice` decides whether MEWS recalculates the stay at the new category's
   * current rate (true) or keeps the existing — often negotiated — price
   * (false). Defaults to true to match the prior always-reprice behaviour.
   */
  async changeReservationCategory(reservationId: number, categoryId: string, assignedResourceId?: string | null, reprice: boolean = true): Promise<{
    status: 'changed'
    reservation_id: number
    category_id: string
    assigned_resource_id: string | null
    assigned_resource_name: string | null
    repriced: boolean
  }> {
    return this.request(`/api/reception/reservations/${reservationId}/change-category`, {
      method: 'POST',
      body: JSON.stringify({ category_id: categoryId, assigned_resource_id: assignedResourceId || null, reprice }),
    })
  }

  /** Pin (or move) a reservation to a specific room in MEWS + mirror it locally. */
  async assignRoom(reservationId: number, assignedResourceId: string, assignedResourceName?: string | null): Promise<{
    status: 'assigned'
    reservation_id: number
    assigned_resource_id: string
    assigned_resource_name: string | null
  }> {
    return this.request(`/api/reception/reservations/${reservationId}/assign-room`, {
      method: 'POST',
      body: JSON.stringify({ assigned_resource_id: assignedResourceId, assigned_resource_name: assignedResourceName ?? undefined }),
    })
  }

  /** Move / extend / shorten a stay — dates only. MEWS re-prices + validates. */
  async updateReservationDates(reservationId: number, dates: { arrival_date?: string; departure_date?: string }): Promise<{
    status: 'updated'
    reservation_id: number
    arrival_date: string
    departure_date: string
  }> {
    return this.request(`/api/reception/reservations/${reservationId}`, {
      method: 'PATCH',
      body: JSON.stringify(dates),
    })
  }

  /**
   * Post an incidental charge (minibar, parking, late fee, …) to a reservation.
   * `tax_code` must be a real MEWS code — the UI offers the codes already on the
   * reservation's bill. Lands on the stay's own bill; MEWS owns the money.
   */
  async postReservationCharge(reservationId: number, body: {
    name: string; gross: number; count?: number; tax_code: string; note?: string
  }): Promise<{ status: 'charged'; order_id: string; bill_id: string | null; bill_reason: string }> {
    return this.request(`/api/reception/reservations/${reservationId}/charge`, {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  /**
   * Mark the reservation's assigned room Inspected in MEWS housekeeping — the
   * override that lets check-in proceed on a room that wasn't inspected yet
   * (MEWS' start call has no bypass flag). Deliberate housekeeping override.
   */
  async inspectRoom(reservationId: number): Promise<{
    status: 'inspected'; reservation_id: number; resource_id: string; resource_name: string | null
  }> {
    return this.request(`/api/reception/reservations/${reservationId}/inspect-room`, { method: 'POST' })
  }

  /** Registration card (Meldeschein): prefill + latest saved card for a reservation. */
  async getRegistration(reservationId: number): Promise<{
    status: string
    reservation: Record<string, any>
    contact: Record<string, any> | null
    registration: { id: number; details: Record<string, any>; signature: string | null; signed_by: string | null; registered_by: string | null; registered_at: string } | null
  }> {
    return this.request(`/api/reception/reservations/${reservationId}/registration`)
  }

  /** Save a signed registration card. `details` is the frozen form snapshot. */
  async saveRegistration(reservationId: number, body: {
    details: Record<string, any>; signature?: string | null; signed_by?: string | null
  }): Promise<{ status: 'saved'; id: number; registered_at: string }> {
    return this.request(`/api/reception/reservations/${reservationId}/registration`, {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  /** Guest search by name, or by email when the term contains '@'. */
  async searchCustomers(q: string): Promise<{ status: string; count: number; customers: any[] }> {
    return this.request(`/api/reception/customers/search?q=${encodeURIComponent(q)}`)
  }

  /** Create a guest profile in MEWS. lastName is the only required field. */
  async createReceptionCustomer(body: Record<string, any>): Promise<any> {
    return this.request('/api/reception/customers', { method: 'POST', body: JSON.stringify(body) })
  }

  /** What can be sold to someone at the desk right now. */
  async getWalkInAvailability(propertyId: number, startDate: string, endDate: string): Promise<{
    status: string
    availability: Array<{ date: string; available: number }>
    /** Per category (absent on older backends) — what the desk picks from. */
    categories?: Array<{ id: string; name: string; tightest: number; days: Array<{ date: string; available: number }> }>
  }> {
    return this.request(
      `/api/reception/availability?property_id=${propertyId}&start_date=${startDate}&end_date=${endDate}`,
    )
  }

  /** Book a walk-in: a direct MEWS reservation, no event and no block. */
  async createWalkIn(body: {
    property_id: number
    customer_id: string
    start_date: string
    end_date: string
    adult_count?: number
    child_count?: number
    resource_category_id?: string
    assigned_resource_id?: string
    rate_id?: string
    notes?: string
    send_confirmation_email?: boolean
  }): Promise<{
    status: string
    reservation_id?: string
    reservation_number?: string
    assigned_resource_id?: string
    mirrored_locally?: boolean
  }> {
    return this.request('/api/reception/walk-in', { method: 'POST', body: JSON.stringify(body) })
  }

  async checkInParticipant(data: CheckInParticipantPayload): Promise<CheckInParticipantResponse> {
    return this.request<CheckInParticipantResponse>('/api/events/checkInParticipant', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async checkInEventParticipants(data: CheckInEventParticipantsPayload): Promise<CheckInEventParticipantsResponse> {
    return this.request<CheckInEventParticipantsResponse>('/api/events/checkInEventParticipants', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async createAvailabilityBlock(data: CreateAvailabilityBlockPayload): Promise<ParticipantResponse> {
    return this.request<ParticipantResponse>('/api/events/createAvailabilityBlock', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateServiceAvailability(data: UpdateServiceAvailabilityPayload): Promise<ParticipantResponse> {
    return this.request<ParticipantResponse>('/api/events/updateServiceAvailability', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async createMewsCustomer(data: CreateMewsCustomerPayload): Promise<ParticipantResponse> {
    return this.request<ParticipantResponse>('/api/events/createMewsCustomer', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async createMewsCompany(data: CreateMewsCompanyPayload): Promise<ParticipantResponse> {
    const res = await this.request<ParticipantResponse>('/api/events/createMewsCompany', {
      method: 'POST',
      body: JSON.stringify(data),
    })
    // The cached MEWS company list (mewsApiService companies.getAll) now lacks it.
    // Drop BOTH the inner source cache and the outer session cache Contacts reads
    // through (mewsCompanyCache, key 'mews:companies:all') — otherwise the new
    // company stays hidden until the outer 10-min TTL expires (T-4010).
    invalidateRef('mews:companies')
    invalidateMewsCompanies()
    return res
  }

  async findOrCreateParty(data: FindOrCreatePartyPayload): Promise<FindOrCreatePartyResponse> {
    return this.request<FindOrCreatePartyResponse>('/api/events/findOrCreateParty', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  // MEWS sync status for an event — one row per entity (pms_block,
  // pms_reservation, event_products, event_package). Used by the hotel
  // tab to show whether each entity is synced, retrying, or stuck.
  async getMewsSyncStatus(eventId: number | string): Promise<{
    event_id: number
    entities: Array<{
      entity_type: string
      entity_id: string
      attempt_no: number
      outcome: 'succeeded' | 'retry_pending' | 'no_availability' | 'capacity_exceeded' | 'failed_permanent' | 'abandoned'
      mews_error_code: string | null
      message: string | null
      attempted_at: string | null
      next_retry_at?: string | null
    }>
  }> {
    return this.request(`/api/events/${eventId}/mews-sync`, { method: 'GET' })
  }

  // Full retry history for a single entity — lazy-loaded when the user
  // expands a row in the sync panel. Can be large for entities that
  // have been retrying for a while.
  async getMewsSyncHistory(eventId: number | string, entityType: string, entityId: number | string): Promise<{
    event_id: number
    entity_type: string
    entity_id: number
    history: Array<{
      attempt_no: number
      outcome: string
      mews_error_code: string | null
      message: string | null
      source: string | null
      attempted_at: string | null
      next_retry_at: string | null
    }>
  }> {
    const qs = `entity=${encodeURIComponent(entityType)}&entity_id=${encodeURIComponent(String(entityId))}`
    return this.request(`/api/events/${eventId}/mews-sync/history?${qs}`, { method: 'GET' })
  }

  async retryReservationSync(reservationId: number): Promise<ParticipantResponse> {
    return this.request<ParticipantResponse>('/api/events/retryReservationSync', {
      method: 'POST',
      body: JSON.stringify({ reservation_id: reservationId }),
    })
  }

  /**
   * `force_all_categories` here is a ONE-RUN override of the event's own
   * cross-tier policy — the route applies it only when it is a real boolean and
   * otherwise leaves the persisted setting alone.
   */
  // ─── Business Central (read-only, via the backend's BC proxy) ─────────
  // The ADG events extension only answers FILTERED queries: a single event by
  // `no`, or a start-date range. Anything unfiltered comes back as
  // RequestDataInvalid, which is why there is no "list all" here.

  /**
   * BC connectivity probe. Distinguishes the two failures that look identical
   * from the browser: 503 `unconfigured` (this stage has no BC credentials) vs
   * 502 (credentials exist, BC itself refused or was unreachable).
   */
  async bcHealth(): Promise<{ status: string; reachable?: boolean; missing?: string[]; message?: string; environment?: string | null }> {
    return this.request('/api/bc/health', { method: 'GET' })
  }

  /**
   * Ask BC which listing shapes its ADG events extension actually accepts.
   *
   * Diagnostic, read-only. Every refusal from that extension looks the same
   * (bare 400 RequestDataInvalid), so this tries the candidate shapes in one
   * round trip instead of one deploy per guess.
   */
  async bcEventsProbe(startDate?: string, endDate?: string): Promise<{ results: Array<{ label: string; ok: boolean; count?: number; error?: string; sampleKeys?: string[]; companyId?: string }>; accepted: string[]; configuredCompany?: string | null; environment?: string | null }> {
    const qs = new URLSearchParams()
    if (startDate) qs.set('startDate', startDate)
    if (endDate) qs.set('endDate', endDate)
    const suffix = qs.toString() ? `?${qs.toString()}` : ''
    return this.request(`/api/bc/events/probe${suffix}`, { method: 'GET', timeoutMs: 120000 })
  }

  /**
   * The enterprise this backend's MEWS tokens authenticate as, plus its
   * Commander host — the single source for every "open in MEWS" deep link.
   */
  async mewsEnterprise(): Promise<{ id: string; name: string; timeZone: string; appBase: string }> {
    return this.request('/api/mews/enterprise', { method: 'GET' })
  }

  /** BC events whose startDate falls in [startDate, endDate). */
  async bcEventsInRange(startDate: string, endDate: string): Promise<{ value: any[]; count: number; mode?: string | null; message?: string }> {
    const qs = new URLSearchParams({ startDate, endDate })
    return this.request(`/api/bc/events?${qs.toString()}`, { method: 'GET' })
  }

  /** One BC event, unprojected — the full row as BC returns it. */
  async bcEvent(no: string): Promise<any> {
    return this.request(`/api/bc/events/${encodeURIComponent(no)}`, { method: 'GET' })
  }

  /** Registered participants for a BC event (pass all=true for every status). */
  async bcEventParticipants(no: string, opts?: { all?: boolean }): Promise<{ value: any[]; count: number }> {
    const qs = opts?.all ? '?all=true' : ''
    return this.request(`/api/bc/events/${encodeURIComponent(no)}/participants${qs}`, { method: 'GET' })
  }

  /** Lecturers for a BC event. */
  async bcEventLecturers(no: string): Promise<{ value: any[]; count: number }> {
    return this.request(`/api/bc/events/${encodeURIComponent(no)}/lecturers`, { method: 'GET' })
  }

  /**
   * One verdict for an event: blocks, reservations, space bookings and product
   * charges, each (except space bookings) confirmed against MEWS. Read-only and
   * safe to call on demand — healEvent below is what repairs.
   */
  async eventHealth(eventId: number): Promise<any> {
    return this.request(`/api/events/${eventId}/health`, { method: 'GET' })
  }

  async healEvent(eventId: number, options?: { auto_create_customers?: boolean; force_all_categories?: boolean }): Promise<HealEventResponse> {
    return this.request<HealEventResponse>('/api/events/healEvent', {
      method: 'POST',
      body: JSON.stringify({ event_id: eventId, ...options }),
    })
  }

  // ─── Azure DevOps proxy ────────────────────────────────────────────────
  // Forwards the call to the operator's Azure DevOps org/project, attaching
  // Basic auth with their PAT. The PAT travels in the request body so it's
  // covered by HTTPS in transit — never logged, never persisted server-side.
  // Same trust model the /api/mews proxy uses for MEWS endpoints.
  async azdoProxy<T = any>(o: {
    org: string
    project?: string
    pat: string
    path: string
    method?: 'GET' | 'POST' | 'PATCH'
    body?: any
    // Azure DevOps's create / update endpoints require
    // `application/json-patch+json` instead of the default `application/json`.
    // Backend honors this field on the proxied request — fall through to
    // application/json when omitted.
    contentType?: string
    // Receiving binary (attachments / images): when true, backend
    // returns `{ contentType, dataBase64 }` instead of parsing the
    // upstream response as JSON. SPA wraps that into a data: URL so
    // images can be displayed without the browser ever seeing the PAT.
    binaryResponse?: boolean
    // Sending binary (uploads): when true, the `body` field is a
    // base64-encoded string; backend decodes before forwarding the
    // raw bytes to Azure with the chosen `contentType`.
    binaryBody?: boolean
  }): Promise<T> {
    return this.request<T>('/api/azdo/proxy', {
      method: 'POST',
      body: JSON.stringify(o),
    })
  }

  // Event-scoped block management. This is the mutation surface used by the
  // hotel tab: resize the remaining hold, extend/replace dates, change room
  // category, or release a block without touching confirmed reservations.
  async manageEventBlock(
    eventId: number,
    body: ManageEventBlockPayload,
    options?: { timeoutMs?: number },
  ): Promise<ManagedBlockResponse> {
    return this.request(`/api/events/${eventId}/blocks`, {
      method: 'PUT',
      body: JSON.stringify(body),
      timeoutMs: options?.timeoutMs,
    })
  }

  async releaseManagedBlock(
    eventId: number,
    blockId: number,
    reason?: string,
    options?: { timeoutMs?: number },
  ): Promise<ManagedBlockResponse> {
    return this.request(`/api/events/${eventId}/blocks`, {
      method: 'DELETE',
      body: JSON.stringify({ block_id: blockId, ...(reason?.trim() ? { reason: reason.trim() } : {}) }),
      timeoutMs: options?.timeoutMs,
    })
  }

  // Legacy PATCH kept for older admin callers. New hotel block edits must use
  // manageEventBlock above because PATCH cannot recategorize or safely replace
  // a shortened/shifted period.
  // PATCH a PMS block — adjust arrival/departure dates, room_count or notes.
  // Mirrors the upsertEvent EXTEND branch on the backend: if the block has a
  // MEWS external_block_id, MEWS is updated first (updateBlockDates +
  // re-issued availabilityAdjustment) and the DB is only persisted on
  // success. Errors surface as BackendApiError with the upstream status:
  //   400 — validation (date format / negative count / category/rate change)
  //   404 — block not found
  //   422 — block has no property_id, or property is missing mews settings
  //   502 — MEWS rejected the change; DB left untouched
  // No-MEWS blocks (no external_block_id) update DB-only and return
  // mews.skipped_reason in the response payload.
  async patchPmsBlock(
    blockId: number,
    body: {
      arrival_date?: string
      departure_date?: string
      room_count?: number
      notes?: string | null
    },
    options?: { timeoutMs?: number },
  ): Promise<{
    block: any
    mews?: { dates_updated?: boolean; adjustment_applied?: boolean; skipped_reason?: string }
  }> {
    return this.request(`/api/events/blocks/${blockId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
      timeoutMs: options?.timeoutMs,
    })
  }

  // ─── Releasable rooms, verified against MEWS ──────────────────────
  //
  // The optimisation page used to compute "N releasable" from our own mirror
  // (Σ pms_blocks.room_count − pickups), so when the mirror had drifted it
  // confidently offered rooms MEWS then refused. This endpoint recomputes the
  // figure from what MEWS actually holds.
  //
  // Fails LOUD, not open: a block whose MEWS state cannot be read reports
  // mewsHeld === null and contributes nothing. Callers must SKIP such events
  // rather than falling back to the computed number — that fallback is the bug.
  //
  // One MEWS round trip PER BLOCK, so this is a confirm-time check for the
  // events the operator actually selected, never a page-load call.
  //   400 EVENT_IDS_REQUIRED / TOO_MANY_EVENTS
  //   502 — MEWS could not be reached at all
  async getReleasableRooms(eventIds: number[]): Promise<ReleasableResponse> {
    const ids = [...new Set(eventIds.filter(n => Number.isFinite(n)))]
    if (ids.length === 0) {
      return { status: 'ok', events: [], total_releasable: 0, partial: false, has_drift: false, failedEventIds: [] }
    }
    // The endpoint caps at 50 ids. Chunking here rather than at the call site
    // keeps every caller correct by default — a page that grows past 50
    // selected events should not start returning 400.
    const CHUNK = 50
    const events: EventReleasable[] = []
    const failedEventIds: number[] = []

    const verifyChunk = (chunk: number[], timeoutMs: number): Promise<ReleasableResponse> =>
      this.request('/api/events/blocks/releasable', {
        method: 'POST',
        body: JSON.stringify({ event_ids: chunk }),
        // Each block is a MEWS read; a full chunk is not a fast request.
        timeoutMs,
      })

    for (let i = 0; i < ids.length; i += CHUNK) {
      const slice = ids.slice(i, i + CHUNK)
      try {
        const res = await verifyChunk(slice, 180_000)
        events.push(...(res?.events || []))
      } catch {
        // EH-0350 / option A. The batched verify failed wholesale (502 /
        // timeout / network). Previously this threw and the ENTIRE release was
        // abandoned — "no event was reduced". Instead, retry each event on its
        // own so one bad or slow event cannot void the healthy siblings, and
        // report the ones that still fail rather than throwing.
        //
        // Guard against a true MEWS outage turning into 50 sequential
        // long-timeout retries: use a shorter per-event timeout and give up
        // after a short run of consecutive failures, marking the rest failed.
        const PER_EVENT_TIMEOUT = 30_000
        const GIVE_UP_AFTER = 3
        let consecutiveFailures = 0
        for (const id of slice) {
          if (consecutiveFailures >= GIVE_UP_AFTER) { failedEventIds.push(id); continue }
          try {
            const res = await verifyChunk([id], PER_EVENT_TIMEOUT)
            events.push(...(res?.events || []))
            consecutiveFailures = 0
          } catch {
            failedEventIds.push(id)
            consecutiveFailures++
          }
        }
      }
    }
    return {
      status: 'ok',
      events,
      total_releasable: events.reduce((s, e) => s + (e.releasable || 0), 0),
      partial: events.some(e => e.partial),
      has_drift: events.some(e => e.hasDrift),
      failedEventIds,
    }
  }

  // Per-block resize — the correct primitive for a multi-block event, and the
  // form this frontend uses. `target` is the desired REMAINING HOLD for that
  // block; the backend converts it to intent as `consumed + target`, so booked
  // rooms are always carried and a target of 0 means "hold nothing further".
  //
  // Deliberately NOT folded into bulkResizeBlocks above: the per-block branch
  // answers with a different shape — `failed` is an ARRAY here and a COUNT
  // there. Sharing one method invites `if (res.failed > 0)`, which against an
  // array is NaN > 0, i.e. always false, and would report every failure as a
  // success.
  //
  // All-or-nothing per event: targets are validated for the whole event before
  // anything is written.
  //   400 — validation (empty events, bad block_id/target, > 500 entries)
  //   403 — caller lacks adg-admin / adg-superadmin
  async resizeBlocksPerBlock(
    body: {
      events: Array<{ event_id: number; blocks: BlockResizeTarget[] }>
      reason?: string
      override_protection?: boolean
    },
    options?: { timeoutMs?: number },
  ): Promise<PerBlockResizeResponse> {
    return this.request('/api/events/blocks/bulkResize', {
      method: 'POST',
      body: JSON.stringify(body),
      timeoutMs: options?.timeoutMs,
    })
  }

  // ─── Reduction protection ("do not reduce") ───────────────────────
  //
  // Mark an event so every room-release path refuses to SHRINK its blocks.
  // Growth and date changes stay allowed — the backend compares the current
  // and proposed sizes rather than intercepting the call, so a protected
  // event can still be enlarged.
  //
  // Caller must hold the hotel-release-approve permission (admins bypass) —
  // the same capability that approves and rejects block releases, deliberately,
  // so "who decides whether rooms go back" is one grant and not two.
  //   400 PROTECTED_REQUIRED — `protected` missing or not a boolean
  //   403 — caller lacks the role
  //   404 — event not found
  // Clearing the flag also clears the audit columns: a stale "set by" on an
  // unprotected event reads as though it were still on.
  async setEventProtection(
    eventId: number,
    isProtected: boolean,
    reason?: string,
  ): Promise<{
    status: 'ok'
    event: {
      id: number
      do_not_reduce: boolean
      do_not_reduce_by: string | null
      do_not_reduce_at: string | null
      do_not_reduce_reason: string | null
    }
  }> {
    return this.request(`/api/events/${eventId}/protection`, {
      method: 'PUT',
      body: JSON.stringify({
        protected: isProtected,
        ...(isProtected && reason?.trim() ? { reason: reason.trim() } : {}),
      }),
    })
  }

  // Bulk resize one or more event blocks in a single backend call.
  // Each event's blocks are adjusted toward a target total room count
  // computed from `mode` + `value`:
  //   'absolute' — every event ends at exactly `value` rooms
  //   'delta'    — add `value` (can be negative) to each event's total
  //   'percent'  — scale each event's total by (1 + value/100)
  // The backend resolves each event_id to its blocks, distributes the
  // target across them, and uses mustAdjustBlockAndVerify per block so
  // MEWS is updated first and DB only persists on success. Partial
  // success is allowed — failures come back in `failed_details`.
  //   400 — validation (empty event_ids, bad mode/value, > 500 ids)
  //   403 — caller lacks adg-admin / adg-superadmin
  async bulkResizeBlocks(
    body: {
      event_ids: number[]
      mode: 'absolute' | 'delta' | 'percent'
      value: number
      reason?: string
    },
    options?: { timeoutMs?: number },
  ): Promise<{
    requested: number
    succeeded: number
    failed: number
    updated: Array<{ event_id: number; block_id: number; old: number; new: number; mews_pushed: boolean }>
    failed_details: Array<{ event_id: number; reason: string }>
  }> {
    return this.request('/api/events/blocks/bulkResize', {
      method: 'POST',
      body: JSON.stringify(body),
      timeoutMs: options?.timeoutMs,
    })
  }

  // ─── Hotel block-release workflow (gated per stream by the property
  //     setting `block_requires_approval_<stream>=true`) ────────────
  //
  // Both endpoints require the caller to hold the `hotel-release-approve`
  // permission (admin roles bypass) and are no-ops when the workflow is
  // disabled property-wide.
  //
  // Approve a 'requested' block. Backend creates the corresponding
  // MEWS availability block first and only marks the row 'created' on
  // success.
  //   409 InvalidState  — block is not in 'requested' state
  //   404 NotFound      — block id doesn't exist for this event
  //   422 BLOCK_AVAILABILITY_SHORTFALL — requested category/date is short
  //   502 BLOCK_APPROVAL_FAILED        — MEWS create failed (block stays requested)
  async approveBlockRelease(eventId: number, blockId: number): Promise<{
    status: 'approved' | 'invalid_state' | 'not_found' | 'no_availability' | 'error'
    pms_block?: any
    message?: string
    details?: {
      requested: number
      available: number
      tightest_day: string
      per_day: Array<{ date: string; available: number }>
    }
  }> {
    return this.request(`/api/events/${eventId}/blocks/${blockId}/approve`, {
      method: 'POST',
    })
  }

  // Reject a 'requested' block. Terminal: the row stays in 'rejected'
  // and no MEWS block is ever created. `reason` is optional but
  // strongly recommended — it's appended to pms_blocks.notes after a
  // `Rejected:` prefix so the operator sees why downstream.
  async rejectBlockRelease(eventId: number, blockId: number, reason: string): Promise<{
    status: 'rejected' | 'invalid_state' | 'not_found' | 'error'
    pms_block?: any
    message?: string
  }> {
    return this.request(`/api/events/${eventId}/blocks/${blockId}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason: reason || undefined }),
    })
  }

  // Re-submit a REJECTED block to the hotel: 'rejected' → 'requested'. MICE-side
  // (plain JWT). Only valid while the block is 'rejected'; anything else returns
  // 'invalid_state'. Edit the block first (a rejected block is editable), then
  // re-request to send the new numbers back into the approval queue.
  async reRequestBlockRelease(eventId: number, blockId: number): Promise<{
    status: 'rerequested' | 'invalid_state' | 'not_found'
    pms_block?: any
    message?: string
  }> {
    return this.request(`/api/events/${eventId}/blocks/${blockId}/re-request`, {
      method: 'POST',
    })
  }

  // Set a REQUESTED block's room category by tier. The backend resolves the
  // tier to a real pms_room_types FK (room_type_id) — the FE never touches the
  // raw column. Only valid while the block is 'requested'.
  async setBlockRoomType(blockId: number, roomType: 'comfort' | 'economy'): Promise<{
    status: 'ok' | 'error'
    block?: any
    error?: string
  }> {
    return this.request(`/api/events/blocks/${blockId}/room-type`, {
      method: 'PATCH',
      body: JSON.stringify({ room_type: roomType }),
    })
  }

  // ─── Observability proxies (App Insights + PostHog) ─────────────────────────
  // Both upstreams are CORS-locked + key-authenticated; the backend holds the
  // credentials and we only send the query. Responses are returned verbatim.

  // App Insights query API → { tables: [{ columns:[{name}], rows:[[...]] }] }.
  // `timespan` is an ISO-8601 duration (e.g. 'PT1H', 'P1D', 'P7D').
  async appInsightsQuery(query: string, timespan = 'P1D'): Promise<any> {
    return this.request('/api/appinsights/query', {
      method: 'POST',
      body: JSON.stringify({ query, timespan }),
    })
  }

  // PostHog HogQL query → { results: [[...]], columns: [...] }.
  // Pass-through credentials: the user supplies their own personal API key +
  // project id in the UI (stored client-side); the backend never persists them.
  async posthogQuery(
    query: string,
    creds: { apiKey: string; projectId: string; host?: string },
  ): Promise<any> {
    return this.request('/api/posthog/query', {
      method: 'POST',
      body: JSON.stringify({
        query,
        api_key: creds.apiKey,
        project_id: creds.projectId,
        host: creds.host,
      }),
    })
  }

  // ─── Pricing (V57+ — product/package price lists with valid_from history) ───────

  // Active product price as of `asOf` (YYYY-MM-DD). Defaults to today.
  async getProductPrice(productId: number, asOf?: string): Promise<{
    product_id: number; as_of: string; unit_price: number; currency: string
  }> {
    const qs = asOf ? `?as_of=${encodeURIComponent(asOf)}` : ''
    return this.request(`/api/events/products/${productId}/price${qs}`, { method: 'GET' })
  }

  // Full price history for a product (admin UI timeline)
  async getProductPriceHistory(productId: number): Promise<{
    product_id: number;
    prices: Array<{ id: number; valid_from: string; unit_price: string; currency: string; notes: string | null; list_name: string | null; created_by: string | null; created_at: string }>
  }> {
    return this.request(`/api/events/products/${productId}/prices`, { method: 'GET' })
  }

  // Validate that a MEWS tax code is actually accepted by the property's
  // enterprise. Returns `{ valid: true }` (cancelled probe order on success)
  // or `{ valid: false, message }` with the verbatim MEWS rejection message.
  async probeTaxCode(propertyId: number, taxCode: string): Promise<{
    valid: boolean;
    message?: string;
    orderId?: string;
    cleanup?: { ok: boolean; message?: string };
    error?: string;
  }> {
    return this.request(`/api/events/taxations/probe`, {
      method: 'POST',
      body: JSON.stringify({ property_id: propertyId, tax_code: taxCode }),
    })
  }

  // Same probe pattern for accounting categories. The candidate MEWS UUID
  // (`accounting_category_id`) is the value stored in
  // `products.mews_accounting_category_id` / `mews_accounting_categories.mews_id`.
  // Backend pairs it with any active local tax code so the rejection (if any)
  // is unambiguously about the accounting category, not the tax code.
  async probeAccountingCategory(propertyId: number, mewsId: string): Promise<{
    valid: boolean;
    message?: string;
    orderId?: string;
    cleanup?: { ok: boolean; message?: string };
    error?: string;
  }> {
    return this.request(`/api/events/accountingCategories/probe`, {
      method: 'POST',
      body: JSON.stringify({ property_id: propertyId, accounting_category_id: mewsId }),
    })
  }

  // Batch lookup — resolves the active price for each product_id at once.
  // Used by the events page to hydrate the catalog with current prices (V77+
  // moved `unit_price` off the products table into product_prices, so a
  // SELECT * FROM products doesn't carry a price anymore).
  async getProductPricesBatch(body: {
    product_ids: number[]; as_of?: string; rate_id?: number; price_list_id?: number;
  }): Promise<{
    as_of: string;
    items: Array<{ product_id: number; unit_price: number; currency: string; rate_id: number | null; price_list_id: number }>;
    missing: number[];
  }> {
    return this.request(`/api/events/products/prices`, {
      method: 'POST', body: JSON.stringify(body)
    })
  }

  // V77: every price row belongs to exactly one price list. Upsert key is
  // (product_id, price_list_id, valid_from); rate_id and list_name are
  // denormalized server-side from the list.
  async addProductPrice(productId: number, body: {
    price_list_id: number;
    valid_from: string;
    unit_price: number;
    currency?: string; notes?: string; created_by?: string;
  }): Promise<any> {
    return this.request(`/api/events/products/${productId}/prices`, {
      method: 'POST', body: JSON.stringify(body)
    })
  }

  // Active package fixed-total as of `asOf`. 404 when no fixed total is set.
  async getPackageFixedTotal(packageId: number, asOf?: string): Promise<{
    package_id: number; as_of: string; fixed_total_price: number; currency: string
  }> {
    const qs = asOf ? `?as_of=${encodeURIComponent(asOf)}` : ''
    return this.request(`/api/events/packages/${packageId}/fixed-total${qs}`, { method: 'GET' })
  }

  async getPackagePriceHistory(packageId: number): Promise<{
    package_id: number;
    prices: Array<{ id: number; valid_from: string; fixed_total_price: string; currency: string; notes: string | null; list_name: string | null; created_by: string | null; created_at: string }>
  }> {
    return this.request(`/api/events/packages/${packageId}/prices`, { method: 'GET' })
  }

  async addPackagePrice(packageId: number, body: {
    price_list_id: number;
    valid_from: string;
    fixed_total_price: number;
    currency?: string; notes?: string; created_by?: string;
  }): Promise<any> {
    return this.request(`/api/events/packages/${packageId}/prices`, {
      method: 'POST', body: JSON.stringify(body)
    })
  }

  // Expand a package into snapshot line items ready to insert into event_products.
  // Backend handles fixed_total_price / per-item override / current price resolution.
  async resolvePackage(packageId: number, body: {
    quantity?: number; as_of?: string; override_total?: number; package_instance_id?: string
  }): Promise<{
    package_id: number; package_name: string; package_instance_id: string;
    total: number; factor: number;
    items: Array<{
      product_id: number; product_name: string;
      unit_price: number; total_price: number; quantity: number;
      currency: string; tax_code: string | null;
      mews_accounting_category_id: string | null;
      mews_product_id: string | null; mews_service_id: string | null;
      charging_type: string | null; product_type: string | null;
      source: 'package'; source_ref: null;
      package_id: number; package_name: string; package_instance_id: string
    }>
  }> {
    return this.request(`/api/events/packages/${packageId}/resolve`, {
      method: 'POST', body: JSON.stringify(body)
    })
  }

  // ─── Hotel room-type pricing (V72) — keyed by (room_type, rate). Hotel rates
  // override the per-night reservation price; this is NOT a billable line item.
  async getRoomTypePrice(roomTypeId: number, rateId: number, asOf?: string, priceListId?: number): Promise<{
    room_type_id: number; rate_id: number; as_of: string; amount: number | null; currency: string | null;
  }> {
    const q = new URLSearchParams({ rate_id: String(rateId) })
    if (asOf) q.set('as_of', asOf)
    if (priceListId != null) q.set('price_list_id', String(priceListId))
    return this.request(`/api/events/room-types/${roomTypeId}/price?${q.toString()}`)
  }

  async getRoomTypePriceHistory(roomTypeId: number, rateId?: number): Promise<{
    room_type_id: number;
    prices: Array<{
      id: number; room_type_id: number; rate_id: number; valid_from: string;
      amount: string; currency: string;
      list_name: string | null; notes: string | null; created_by: string | null; created_at: string;
    }>;
  }> {
    const qs = rateId != null ? `?rate_id=${rateId}` : ''
    return this.request(`/api/events/room-types/${roomTypeId}/prices${qs}`)
  }

  async addRoomTypePrice(roomTypeId: number, body: {
    price_list_id: number;
    valid_from: string;
    amount: number;
    currency?: string;
    notes?: string;
    created_by?: string;
  }): Promise<any> {
    return this.request(`/api/events/room-types/${roomTypeId}/prices`, {
      method: 'POST', body: JSON.stringify(body)
    })
  }

  // V77: layered price-list model. kind='time' creates a rate-independent
  // override list (applies to all rates); kind='rate' creates a rate-scoped
  // override (rate_id required); kind='base' is auto-created by the migration
  // — re-creating one returns 409. Override lists start empty.
  async createPriceList(body: {
    property_id: number;
    kind: 'base' | 'time' | 'rate';
    rate_id?: number;
    list_name: string;
    valid_from: string;
    description?: string;
    created_by?: string;
  }): Promise<{
    id: number; property_id: number; rate_id: number | null; list_name: string;
    is_base: boolean; is_active: boolean; valid_from: string;
  }> {
    return this.request('/api/events/price-lists', {
      method: 'POST', body: JSON.stringify(body)
    })
  }

  // ─── Native PMS master data (/api/pms) ─────────────────────────────────
  // Only meaningful when getPmsInfo().capabilities.masterData is true (see
  // usePmsInfo); on a MEWS backend every write answers 409. Ids are the
  // main-schema mirror ids the tabs already hold (pms_room_types.id,
  // pms_rooms.id, rates.id, tax_codes.id).

  async getPmsInfo(): Promise<{ provider: string; capabilities: Record<string, boolean> }> {
    return this.request('/api/pms/info', { timeoutMs: 15_000 })
  }

  async pmsListRoomCategories(propertyId: number): Promise<any[]> {
    return this.request(`/api/pms/room-categories?property_id=${propertyId}`)
  }
  async pmsCreateRoomCategory(propertyId: number, body: Record<string, unknown>): Promise<any> {
    return this.request('/api/pms/room-categories', { method: 'POST', body: JSON.stringify({ ...body, property_id: propertyId }) })
  }
  async pmsUpdateRoomCategory(propertyId: number, id: number, body: Record<string, unknown>): Promise<any> {
    return this.request(`/api/pms/room-categories/${id}`, { method: 'PATCH', body: JSON.stringify({ ...body, property_id: propertyId }) })
  }

  async pmsListRooms(propertyId: number): Promise<any[]> {
    return this.request(`/api/pms/rooms?property_id=${propertyId}`)
  }
  async pmsCreateRoom(propertyId: number, body: Record<string, unknown>): Promise<any> {
    return this.request('/api/pms/rooms', { method: 'POST', body: JSON.stringify({ ...body, property_id: propertyId }) })
  }
  async pmsUpdateRoom(propertyId: number, id: number, body: Record<string, unknown>): Promise<any> {
    return this.request(`/api/pms/rooms/${id}`, { method: 'PATCH', body: JSON.stringify({ ...body, property_id: propertyId }) })
  }

  async pmsCreateRatePlan(propertyId: number, body: Record<string, unknown>): Promise<any> {
    return this.request('/api/pms/rate-plans', { method: 'POST', body: JSON.stringify({ ...body, property_id: propertyId }) })
  }
  async pmsUpdateRatePlan(propertyId: number, id: number, body: Record<string, unknown>): Promise<any> {
    return this.request(`/api/pms/rate-plans/${id}`, { method: 'PATCH', body: JSON.stringify({ ...body, property_id: propertyId }) })
  }
  async pmsGetRatePrices(propertyId: number, rateId: number, range: { from: string; to: string; category_id?: number }): Promise<Array<{
    category_id: number | null; category_name: string | null; night: string; amount: number; currency: string
  }>> {
    const q = new URLSearchParams({ property_id: String(propertyId), from: range.from, to: range.to })
    if (range.category_id !== undefined) q.set('category_id', String(range.category_id))
    return this.request(`/api/pms/rate-plans/${rateId}/prices?${q.toString()}`)
  }
  async pmsUpsertRatePrices(propertyId: number, rateId: number, body: {
    category_id: number; from: string; to: string; amount: number; currency: string
  }): Promise<{ nights: number }> {
    return this.request(`/api/pms/rate-plans/${rateId}/prices`, { method: 'PUT', body: JSON.stringify({ ...body, property_id: propertyId }) })
  }

  async pmsListRatePlans(propertyId: number): Promise<any[]> {
    return this.request(`/api/pms/rate-plans?property_id=${propertyId}`)
  }
  async pmsListTaxCodes(propertyId: number): Promise<any[]> {
    return this.request(`/api/pms/tax-codes?property_id=${propertyId}`)
  }
  async pmsCreateTaxCode(propertyId: number, body: Record<string, unknown>): Promise<any> {
    return this.request('/api/pms/tax-codes', { method: 'POST', body: JSON.stringify({ ...body, property_id: propertyId }) })
  }
  async pmsUpdateTaxCode(propertyId: number, id: number, body: Record<string, unknown>): Promise<any> {
    return this.request(`/api/pms/tax-codes/${id}`, { method: 'PATCH', body: JSON.stringify({ ...body, property_id: propertyId }) })
  }

  // Property-level settings of the native property (calendar + currency).
  async pmsGetProperty(propertyId: number): Promise<{
    name: string; code: string; timezone: string; currency: string; checkin_hour: number; checkout_hour: number
  }> {
    return this.request(`/api/pms/property?property_id=${propertyId}`)
  }
  async pmsUpdateProperty(propertyId: number, body: Record<string, unknown>): Promise<any> {
    return this.request('/api/pms/property', { method: 'PATCH', body: JSON.stringify({ ...body, property_id: propertyId }) })
  }

  // Products live only in eventhub-pms (native ids); the Products tab
  // imports them like MEWS products through products/getAll.
  async pmsListProducts(propertyId: number): Promise<any[]> {
    return this.request(`/api/pms/products?property_id=${propertyId}`)
  }
  async pmsCreateProduct(propertyId: number, body: Record<string, unknown>): Promise<any> {
    return this.request('/api/pms/products', { method: 'POST', body: JSON.stringify({ ...body, property_id: propertyId }) })
  }
  async pmsUpdateProduct(propertyId: number, id: number, body: Record<string, unknown>): Promise<any> {
    return this.request(`/api/pms/products/${id}`, { method: 'PATCH', body: JSON.stringify({ ...body, property_id: propertyId }) })
  }

  // Accounting categories: mirror rows are mews_accounting_categories.
  async pmsListAccountingCategories(propertyId: number): Promise<any[]> {
    return this.request(`/api/pms/accounting-categories?property_id=${propertyId}`)
  }
  async pmsCreateAccountingCategory(propertyId: number, body: Record<string, unknown>): Promise<any> {
    return this.request('/api/pms/accounting-categories', { method: 'POST', body: JSON.stringify({ ...body, property_id: propertyId }) })
  }
  async pmsUpdateAccountingCategory(propertyId: number, id: number, body: Record<string, unknown>): Promise<any> {
    return this.request(`/api/pms/accounting-categories/${id}`, { method: 'PATCH', body: JSON.stringify({ ...body, property_id: propertyId }) })
  }

  // Guest + company accounts of the native PMS (native ids).
  async pmsListAccounts(propertyId: number, filter: { kind?: 'guest' | 'company'; q?: string; limit?: number } = {}): Promise<any[]> {
    const q = new URLSearchParams({ property_id: String(propertyId) })
    if (filter.kind) q.set('kind', filter.kind)
    if (filter.q) q.set('q', filter.q)
    if (filter.limit) q.set('limit', String(filter.limit))
    return this.request(`/api/pms/accounts?${q.toString()}`)
  }
  async pmsCreateAccount(propertyId: number, body: Record<string, unknown>): Promise<any> {
    return this.request('/api/pms/accounts', { method: 'POST', body: JSON.stringify({ ...body, property_id: propertyId }) })
  }
  async pmsUpdateAccount(propertyId: number, id: number, body: Record<string, unknown>): Promise<any> {
    return this.request(`/api/pms/accounts/${id}`, { method: 'PATCH', body: JSON.stringify({ ...body, property_id: propertyId }) })
  }

  // ─── Price-list registry ──────────────────────────────────────────────
  async listPriceLists(opts?: { property_id?: number; rate_id?: number; include_inactive?: boolean }): Promise<{
    price_lists: Array<{
      id: number;
      property_id: number;
      rate_id: number | null;
      list_name: string;
      is_base: boolean;
      is_active: boolean;
      description: string | null;
      valid_from: string | null;
      created_by: string | null;
      created_at: string;
      updated_at: string;
      rate_name: string | null;
      rate_stream: string | null;
      rate_is_default: boolean | null;
    }>;
  }> {
    const q = new URLSearchParams()
    if (opts?.property_id != null) q.set('property_id', String(opts.property_id))
    if (opts?.rate_id != null) q.set('rate_id', String(opts.rate_id))
    if (opts?.include_inactive) q.set('include_inactive', 'true')
    const qs = q.toString() ? `?${q.toString()}` : ''
    return this.request(`/api/events/price-lists${qs}`)
  }

  async togglePriceList(id: number, body: { is_active?: boolean; description?: string; list_name?: string; valid_from?: string }): Promise<any> {
    return this.request(`/api/events/price-lists/${id}`, {
      method: 'PATCH', body: JSON.stringify(body)
    })
  }

  // Atomic product create: backend wraps INSERT products + initial product_prices
  // row in one transaction. Required for custom products (must have ≥1 price).
  async createProduct(body: {
    property_id: number | string;
    name: string;
    description?: string | null;
    sku?: string | null;
    category_id?: number | string | null;
    currency?: string;
    charging_type?: string | null;
    is_stockable?: boolean;
    is_active?: boolean;
    sort_order?: number;
    tax_code?: string | null;
    mews_accounting_category_id?: string | null;
    product_type?: 'custom' | 'internal' | 'mews';
    // Required when product_type='custom': sets the first product_prices row.
    // initial_price_list_id omitted → lands in the property's base list.
    initial_price_gross?: number;
    initial_valid_from?: string;
    initial_price_list_id?: number;
    created_by?: string;
  }): Promise<{ id: number; [k: string]: any }> {
    return this.request('/api/events/products', {
      method: 'POST', body: JSON.stringify(body)
    })
  }

  async getMissingPrices(opts?: { as_of?: string; property_id?: number }): Promise<{
    as_of: string;
    property_id: number | null;
    products: Array<{ id: number; name: string; property_id: number }>;
    packages: Array<{ id: number; name: string; property_id: number; missing_fixed_total: boolean; has_unpriced_item: boolean }>;
    room_types: Array<{ id: number; name: string; property_id: number; rate_id: number; rate_name: string }>;
  }> {
    const params = new URLSearchParams()
    if (opts?.as_of) params.set('as_of', opts.as_of)
    if (opts?.property_id != null) params.set('property_id', String(opts.property_id))
    const qs = params.toString() ? `?${params.toString()}` : ''
    return this.request(`/api/events/products/missing-prices${qs}`, { method: 'GET' })
  }

  async getSystemStatus(): Promise<SystemStatusResponse> {
    return this.request<SystemStatusResponse>('/api/system/status', {
      method: 'GET',
    })
  }

  async getSystemCheck(): Promise<SystemCheckResponse> {
    return this.request<SystemCheckResponse>('/api/system/check', {
      method: 'GET',
    })
  }

  /**
   * Surface MEWS sync worker health + queue-state snapshot. Distinct
   * from `getMewsSyncStatus(eventId)` above, which is the per-event
   * entities view. `worker` is null on processes that never instantiated
   * one (test runs, MEWS_SYNC_WORKER_ENABLED=false at startup).
   */
  async getMewsWorkerStatus(): Promise<MewsSyncStatusResponse> {
    return this.request<MewsSyncStatusResponse>('/api/system/sync-status', {
      method: 'GET',
    })
  }

  async aiChat(data: AiChatRequest): Promise<AiChatResponse> {
    return this.request<AiChatResponse>('/api/ai/chat', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  // ── File Management ─────────────────────────────────────────

  async getFileUploadUrl(data: FileUploadUrlRequest): Promise<FileUploadUrlResponse> {
    return this.request<FileUploadUrlResponse>('/api/files/upload-url', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async registerFile(data: FileRegisterRequest): Promise<{ file: FileRecord; assignment?: FileAssignment }> {
    return this.request<{ file: FileRecord; assignment?: FileAssignment }>('/api/files', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async getEntityFiles(entityType: string, entityId: number | string): Promise<FileEntityResponse> {
    return this.request<FileEntityResponse>(`/api/files/entity/${entityType}/${entityId}`, {
      method: 'GET',
    })
  }

  async getFileDownloadUrl(fileId: number): Promise<FileDownloadResponse> {
    return this.request<FileDownloadResponse>(`/api/files/${fileId}/download-url`, {
      method: 'GET',
    })
  }

  /**
   * Proxy upload (T-2845): send the raw file bytes to the backend, which uploads
   * to Blob Storage over the private VNet path and registers + assigns the file
   * in one round trip. Replaces the SAS three-step (getFileUploadUrl →
   * uploadFileToBlob → registerFile), whose browser↔blob PUT breaks the moment
   * storage publicNetworkAccess is disabled (T-2837).
   *
   * Not routed through `request()` because the body is raw bytes, not JSON — but
   * the auth header is composed identically (upstream JWT), so it inherits the
   * same apiKeyAuthWithActor + requireJwt guard as every other /api/* call.
   */
  async uploadFileProxy(
    params: FileProxyUploadParams,
    file: Blob,
  ): Promise<{ file: FileRecord; assignment?: FileAssignment }> {
    const query = new URLSearchParams({
      property_id: String(params.property_id),
      entity_type: params.entity_type,
      entity_id: String(params.entity_id),
      file_name: params.file_name,
      mime_type: params.mime_type,
    })
    if (params.uploaded_by) query.set('uploaded_by', params.uploaded_by)
    if (params.tag) query.set('tag', params.tag)

    const token = await getUpstreamJwt()
    const response = await fetch(`${this.baseUrl}/api/files/upload?${query.toString()}`, {
      method: 'POST',
      headers: {
        'Content-Type': params.mime_type || 'application/octet-stream',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: file,
    })
    if (!response.ok) {
      const text = await response.text().catch(() => '')
      let message = `File upload failed: ${response.status}`
      try {
        const parsed = JSON.parse(text)
        message = parsed.error || parsed.message || message
      } catch {
        if (text) message = text
      }
      throw new BackendApiError(message, response.status)
    }
    return response.json()
  }

  /**
   * Proxy download (T-2845): fetch the blob bytes back through the backend with
   * auth, and hand the caller a browser object URL suitable for an <img src>.
   * The proxy GET requires a Bearer token, so — unlike the old SAS download_url
   * — its URL can't be dropped straight into markup; the bytes must be fetched
   * here and wrapped. Callers should URL.revokeObjectURL() when done to avoid
   * leaking the object URL.
   */
  /**
   * The digital-signage feed as the signs fetch it (tab-separated text, today →
   * +72 h). request() parses JSON, so this reads the body as text.
   */
  async getSignageFeed(): Promise<string> {
    const token = await getUpstreamJwt()
    const response = await fetch(`${this.baseUrl}/api/space-bookings/signage`, {
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    })
    if (!response.ok) {
      throw new BackendApiError(`Signage feed failed: ${response.status}`, response.status)
    }
    return response.text()
  }

  async getFileObjectUrl(fileId: number): Promise<string> {
    const token = await getUpstreamJwt()
    const response = await fetch(`${this.baseUrl}/api/files/${fileId}/download`, {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    })
    if (!response.ok) {
      throw new BackendApiError(`File download failed: ${response.status}`, response.status)
    }
    const blob = await response.blob()
    return URL.createObjectURL(blob)
  }

  async deleteFile(fileId: number): Promise<void> {
    return this.request<void>(`/api/files/${fileId}`, {
      method: 'DELETE',
    })
  }

  /**
   * Post the event's pending product lines to MEWS.
   *
   * Without `eventProductIds` the run is event-wide — every pending product
   * line of the event, both panels and every session ("Sync all"). With it
   * the backend stamps, judges and posts ONLY those `event_products` rows
   * (the row button); the siblings of a rule bundle are pulled in on the
   * backend because a bundle is one MEWS order. Backend PR #609.
   */
  async chargeProducts(eventId: string | number, eventProductIds?: number[]): Promise<{ status: string; message?: string; [key: string]: any }> {
    return this.request<{ status: string; message?: string; [key: string]: any }>(`/api/events/chargeProducts/${eventId}`, {
      method: 'POST',
      ...(eventProductIds ? { body: JSON.stringify({ event_product_ids: eventProductIds }) } : {}),
    })
  }

  /**
   * The signed-in user as the backend sees them.
   *
   * `roles` is authoritative — it comes from `user_roles` and is what drives
   * PostgREST and RLS. `provider_roles` / `provider_groups` are DISPLAY ONLY:
   * whatever the identity provider put in the token. They are groundwork for
   * ADG's Entra admins owning roles there, and are normally empty today because
   * the app registration defines no app roles.
   */
  async getMe(): Promise<{
    userId?: string
    email?: string
    name?: string
    provider?: 'entra' | 'supertokens'
    roles?: string[]
    provider_roles?: string[]
    provider_groups?: string[]
  }> {
    return this.request('/api/auth/me', { method: 'GET' })
  }

  /**
   * Re-apply the product rules to every eligible participant on ONE event.
   *
   * Rules are forward-only by design: editing a rule does NOT sweep
   * participants already on an event. This is the deliberate manual override
   * for the cases where an operator does want the existing ones charged — a
   * button, never a trigger on rule save.
   *
   * ALWAYS preview first (`dryRun: true`). A real run posts order items to
   * MEWS, and separately posted orders do not cancel cleanly there, so an
   * over-broad apply is manual and lossy to undo. The preview returns
   * `reservations_eligible` — confirmed participants WITH a room, which is what
   * the rules engine actually runs against — and charges nothing.
   */
  async applyRules(
    eventId: string | number,
    opts?: { dryRun?: boolean; rateId?: number; priceListName?: string | null },
  ): Promise<{
    event_id: number
    reservations_total: number
    reservations_eligible: number
    dry_run?: boolean
    warnings?: any[]
  }> {
    return this.request(`/api/events/applyRules/${eventId}`, {
      method: 'POST',
      body: JSON.stringify({
        dry_run: opts?.dryRun === true,
        ...(opts?.rateId != null ? { rate_id: opts.rateId } : {}),
        ...(opts?.priceListName !== undefined ? { price_list_name: opts.priceListName } : {}),
      }),
    })
  }

  /**
   * Ask MEWS whether this event's charged lines are really charged there.
   *
   * Read-only. `is_charged_to_mews` only records that a charge run once
   * succeeded; the order can be cancelled on the MEWS side afterwards, so the
   * panel's green check has to come from MEWS itself.
   */
  async verifyCharges(eventId: string | number): Promise<{
    status: string
    lines: Array<{
      source_type: 'product' | 'package'
      source_id: number
      verdict: 'ok' | 'partial' | 'missing' | 'unknown'
      expected: number
      confirmed: number
      missing_payers: string[]
      /** The same payers as pms_reservations.ids; empty for a customer-level payer. */
      missing_payer_ids?: number[]
      /** Each payer account this line bills, with MEWS id + name and whether MEWS holds it live. T-3961: lets the card name a day-guest payer with no invoice-owner link. */
      payers?: Array<{ pms_reservation_id: number | null; account_id: string; account_name: string | null; live: boolean }>
      reason?: string
    }>
    errors: string[]
  }> {
    return this.request(`/api/events/verifyCharges/${eventId}`, { method: 'GET' })
  }

  /**
   * Cancel ONE charged product/package line for only the named participants.
   *
   * The whole-row removal (status 'cancel' + chargeProducts) remains "cancel
   * for everyone"; this is the partial counterpart, and the backend rewrites
   * the line's charge targets so the released people are not re-charged.
   */
  async releaseCharges(
    eventId: string | number,
    body: { source_type: 'product' | 'package'; source_id: number; reservation_ids: number[] },
  ): Promise<{ status: string; released?: number; remaining?: number[]; cancelled?: boolean; errors?: string[]; [key: string]: any }> {
    return this.request(`/api/events/releaseCharges/${eventId}`, {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  async chargePackage(eventId: string | number, packageId: string | number): Promise<{ status: string; message?: string; [key: string]: any }> {
    return this.request<{ status: string; message?: string; [key: string]: any }>(`/api/events/chargePackage/${eventId}/${packageId}`, {
      method: 'POST',
    })
  }

  async uploadFileToBlob(uploadUrl: string, file: File, mimeType: string): Promise<void> {
    // Use XMLHttpRequest to bypass SuperTokens fetch interceptor.
    // Azure Blob Storage rejects preflight requests with auth headers.
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      xhr.open('PUT', uploadUrl, true)
      xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob')
      xhr.setRequestHeader('Content-Type', mimeType)
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) resolve()
        else reject(new Error(`Blob upload failed: ${xhr.status}`))
      }
      xhr.onerror = () => reject(new Error('Blob upload network error'))
      xhr.send(file)
    })
  }

  async aiChatStream(
    data: AiChatRequest,
    onToken: (token: string) => void,
    onDone: () => void,
    onError: (error: string) => void,
  ): Promise<void> {
    const url = `${this.baseUrl}/api/ai/chat/stream`
    try {
      const token = await getUpstreamJwt()
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(data),
      })

      if (!response.ok) {
        const text = await response.text()
        onError(`AI error: ${response.status} - ${text}`)
        return
      }

      const reader = response.body?.getReader()
      if (!reader) {
        onError('No response stream')
        return
      }

      const decoder = new TextDecoder()
      let buffer = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6)
            if (data === '[DONE]') {
              onDone()
              return
            }
            try {
              const parsed = JSON.parse(data)
              const text = parsed.content || parsed.token || ''
              if (text) onToken(text)
            } catch {
              if (data.trim()) onToken(data)
            }
          }
        }
      }
      onDone()
    } catch (err: any) {
      onError(err.message || 'Stream connection failed')
    }
  }

  // ── Maya AI Assistant API ─────────────────────────────────────

  // Conversations
  async mayaGetConversations(userId: string, assistantId?: number): Promise<any> {
    const params = new URLSearchParams({ user_id: userId })
    if (assistantId) params.set('assistant_id', String(assistantId))
    return this.request<any[]>(`/api/maya/conversations?${params}`, { method: 'GET' })
  }

  async mayaCreateConversation(title: string, userId: string, assistantId?: number): Promise<any> {
    return this.request<any>('/api/maya/conversations', { method: 'POST', body: JSON.stringify({ title, user_id: userId, assistant_id: assistantId }) })
  }

  async mayaDeleteConversation(id: string): Promise<any> {
    return this.request<any>(`/api/maya/conversations/${id}`, { method: 'DELETE' })
  }

  async mayaArchiveConversation(id: string): Promise<any> {
    return this.request<any>(`/api/maya/conversations/${id}`, { method: 'PATCH', body: JSON.stringify({ is_archived: true }) })
  }

  async mayaGetMessages(conversationId: string): Promise<any> {
    return this.request<any[]>(`/api/maya/conversations/${conversationId}/messages`, { method: 'GET' })
  }

  // Chat (DB-driven, uses soul/mission/skills/RAG)
  async mayaChatStream(
    data: { messages: AiChatMessage[]; property_id?: number; conversation_id?: string; assistant_id?: number; user_id?: string },
    onToken: (token: string) => void,
    onDone: () => void,
    onError: (error: string) => void,
  ): Promise<void> {
    const url = `${this.baseUrl}/api/maya/chat/stream`
    try {
      const token = await getUpstreamJwt()
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(data),
      })
      if (!response.ok) {
        const text = await response.text()
        onError(`Maya error: ${response.status} - ${text}`)
        return
      }
      const reader = response.body?.getReader()
      if (!reader) { onError('No response stream'); return }
      const decoder = new TextDecoder()
      let buffer = ''
      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const d = line.slice(6)
            if (d === '[DONE]') { onDone(); return }
            try {
              const parsed = JSON.parse(d)
              const text = parsed.content || parsed.token || ''
              if (text) onToken(text)
            } catch {
              if (d.trim()) onToken(d)
            }
          }
        }
      }
      onDone()
    } catch (err: any) {
      onError(err.message || 'Stream connection failed')
    }
  }

  // Assistants (combined soul + mission)
  async mayaGetAssistants(propertyId?: number): Promise<any> {
    const qs = propertyId ? `?property_id=${propertyId}` : ''
    return this.request<any[]>(`/api/maya/assistants${qs}`, { method: 'GET' })
  }

  async mayaGetAssistant(id: number): Promise<any> {
    return this.request<any>(`/api/maya/assistants/${id}`, { method: 'GET' })
  }

  async mayaCreateAssistant(data: any): Promise<any> {
    return this.request<any>('/api/maya/assistants', { method: 'POST', body: JSON.stringify(data) })
  }

  /**
   * What Maya may do for this user, and why — the resolved capability plus the
   * two switches behind it (the assistant's write flag, the role permission).
   * Without both inputs a refusal is indistinguishable from a limitation.
   */
  async mayaCapabilities(propertyId: number, assistantId?: number | null): Promise<{
    canRead: boolean; canCreate: boolean; canModify: boolean
    assistant_id: number | null; assistant_name: string | null
    assistant_can_write: boolean; role_can_create: boolean; role_can_modify: boolean
  }> {
    const qs = new URLSearchParams({ property_id: String(propertyId) })
    if (assistantId) qs.set('assistant_id', String(assistantId))
    return this.request(`/api/maya/chat/capabilities?${qs.toString()}`, { method: 'GET' })
  }

  async mayaUpdateAssistant(id: number, data: any): Promise<any> {
    return this.request<any>(`/api/maya/assistants/${id}`, { method: 'PUT', body: JSON.stringify(data) })
  }

  async mayaDeleteAssistant(id: number): Promise<any> {
    return this.request<any>(`/api/maya/assistants/${id}`, { method: 'DELETE' })
  }

  async mayaSetDefaultAssistant(id: number): Promise<any> {
    return this.request<any>(`/api/maya/assistants/${id}/default`, { method: 'PATCH' })
  }

  // Skills
  async mayaGetSkills(propertyId?: number, assistantId?: number): Promise<any> {
    const params = new URLSearchParams()
    if (propertyId) params.set('property_id', String(propertyId))
    if (assistantId) params.set('assistant_id', String(assistantId))
    const qs = params.toString()
    return this.request<any>(`/api/maya/skills${qs ? '?' + qs : ''}`, { method: 'GET' })
  }

  async mayaCreateSkill(data: any): Promise<any> {
    return this.request<any>('/api/maya/skills', { method: 'POST', body: JSON.stringify(data) })
  }

  async mayaUpdateSkill(id: number, data: any): Promise<any> {
    return this.request<any>(`/api/maya/skills/${id}`, { method: 'PUT', body: JSON.stringify(data) })
  }

  async mayaDeleteSkill(id: number): Promise<any> {
    return this.request<any>(`/api/maya/skills/${id}`, { method: 'DELETE' })
  }

  // Accounts
  async mayaGetAccounts(propertyId?: number): Promise<any> {
    const qs = propertyId ? `?property_id=${propertyId}` : ''
    return this.request<any[]>(`/api/maya/accounts${qs}`, { method: 'GET' })
  }

  async mayaCreateAccount(data: any): Promise<any> {
    return this.request<any>('/api/maya/accounts', { method: 'POST', body: JSON.stringify(data) })
  }

  async mayaUpdateAccount(id: number, data: any): Promise<any> {
    return this.request<any>(`/api/maya/accounts/${id}`, { method: 'PUT', body: JSON.stringify(data) })
  }

  async mayaDeleteAccount(id: number): Promise<any> {
    return this.request<any>(`/api/maya/accounts/${id}`, { method: 'DELETE' })
  }

  // Documents / Knowledge
  async mayaGetDocuments(propertyId?: number): Promise<any> {
    const qs = propertyId ? `?property_id=${propertyId}` : ''
    return this.request<any[]>(`/api/maya/documents${qs}`, { method: 'GET' })
  }

  async mayaUploadDocument(data: any): Promise<any> {
    return this.request<any>('/api/maya/documents', { method: 'POST', body: JSON.stringify(data) })
  }

  async mayaDeleteDocument(id: number): Promise<any> {
    return this.request<any>(`/api/maya/documents/${id}`, { method: 'DELETE' })
  }

  async mayaReindexDocument(id: number): Promise<any> {
    return this.request<any>(`/api/maya/documents/${id}/reindex`, { method: 'POST' })
  }

  // Agents
  async mayaGetAgents(propertyId?: number): Promise<any[]> {
    const qs = propertyId ? `?property_id=${propertyId}` : ''
    return this.request<any[]>(`/api/maya/agents${qs}`, { method: 'GET' })
  }

  async mayaCreateAgent(data: any): Promise<any> {
    return this.request<any>('/api/maya/agents', { method: 'POST', body: JSON.stringify(data) })
  }

  async mayaUpdateAgent(id: number, data: any): Promise<any> {
    return this.request<any>(`/api/maya/agents/${id}`, { method: 'PUT', body: JSON.stringify(data) })
  }

  async mayaDeleteAgent(id: number): Promise<any> {
    return this.request<any>(`/api/maya/agents/${id}`, { method: 'DELETE' })
  }

  async mayaToggleAgent(id: number, enabled: boolean): Promise<any> {
    return this.request<any>(`/api/maya/agents/${id}`, { method: 'PATCH', body: JSON.stringify({ enabled }) })
  }

  // Usage
  async mayaGetUsage(): Promise<any> {
    return this.request<any>('/api/maya/usage', { method: 'GET' })
  }

  async mayaGetUsageSummary(): Promise<any> {
    return this.request<any>('/api/maya/usage/summary', { method: 'GET' })
  }

  /**
   * Live, date-specific commercial terms for rates configured on a property.
   * The backend combines local rate scope with MEWS restrictions, pricing and
   * effective cancellation policies; this call is read-only.
   */
  async listApplicableRates(query: ApplicableRatesQuery): Promise<ApplicableRatesResponse> {
    const params = new URLSearchParams({
      property_id: String(query.property_id),
      start_date: query.start_date,
      end_date: query.end_date,
      kind: query.kind,
    })
    if (query.room_type_id != null) params.set('room_type_id', String(query.room_type_id))
    if (query.stream) params.set('stream', query.stream)
    return this.request<ApplicableRatesResponse>(`/api/events/rates/applicable?${params.toString()}`, {
      method: 'GET',
    })
  }

  // Refresh every locally-mapped rate from MEWS. Besides the canonical name,
  // the backend persists Rate.GroupId and Rate.ServiceId so the UI can
  // distinguish hotel/dayguest ownership without another live lookup.
  // Admin-only on the backend.
  async syncRatesFromMews(propertyId: number): Promise<{
    status: 'ok' | 'error'
    message?: string
    summary?: {
      total: number
      updated: number
      renamed: number
      metadata_updated: number
      unchanged: number
      unmapped: number
      renames: Array<{ rate_id: number; mews_rate_id: string; from: string; to: string }>
    }
  }> {
    return this.request('/api/events/sync-rates-from-mews', {
      method: 'POST',
      body: JSON.stringify({ property_id: propertyId }),
    })
  }

  /** @deprecated Use syncRatesFromMews; retained for older callers. */
  async syncRateNamesFromMews(propertyId: number) {
    return this.syncRatesFromMews(propertyId)
  }
}

let backendApiInstance: BackendApiService | null = null

export function useBackendApi(): BackendApiService {
  if (!backendApiInstance) {
    backendApiInstance = new BackendApiService()
  }
  return backendApiInstance
}

export const backendApi = useBackendApi()
